import { inject, Injectable, signal } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import {
  AccountModel,
  AccountRequestsClient,
  InvestmentModel,
  InvestmentModelsClient,
  OrionConnectAccountSleeveModel,
  OrionConnectClient,
  OrionConnectTradingModelAggModel,
  OrionEclipseAccountDetailModel,
  OrionEclipseAccountHoldingsResponseModel,
  OrionEclipseClient,
  OrionEclipseModelListResponseModel,
  OrionEclipseSecurityListResponseModel,
  OrionEclipsePortfolioDetailModel,
  SchwabClient,
} from 'src/app/core/clients/generated/client';
import {
  customHoldingsEclipseModelID,
  customHoldingsModelID,
  customHoldingsModelName,
} from '../../../account-request-workflows/shared/constants/custom-holdings-model-id.constant';
import {
  AccountInfo,

  ReallocationV2Data,
  DeliveryFrequencyOption,
  DeliveryMethodOption,
  FundingOptions,
  ModelMinimums,
  ModelOption,
  PortfolioContext,
  Position,
  SecurityOption,
  SleeveAllocation,
  StandingInstructionOption,
} from '../models/reallocation-v2.models';

/**
 * What the funding wave needs to know, for the request types that have a funding step.
 *
 * Deliberately not the request type: the service does not need to know what kind of request is
 * being filed, only which direction the money moves — and keeping it that way avoids importing
 * the store's enum into the service the store imports.
 */
export interface FundingLoadOptions {
  /** `INCOMING` for a contribution, `OUTGOING` for a distribution or a close. */
  direction: 'INCOMING' | 'OUTGOING';
}

/**
 * The standing-authorization entry meaning "somewhere this account has no authorization for".
 *
 * The literal string V1 uses, and it doubles as the key because Schwab supplies no id — so it
 * reaches `DeliveryInstruction` verbatim and the trade team reads the same words the advisor
 * picked.
 */
export const OTHER_STANDING_INSTRUCTION_KEY = 'Other (New Account)';

/**
 * Which wave of the initial load is in flight.
 *
 * The load is three **sequential** waves, and the depth is inherent: wave 2 needs the
 * `portfolioId` wave 1 returns, and wave 3 needs the `registrationId` only wave 2's
 * portfolio detail supplies. Reporting the real boundaries means the progress display
 * cannot claim something is done while its request is still out.
 */
export type ReallocationV2LoadStage = 'account' | 'positions' | 'sleeves' | 'done';

/** Connect's `sleeveType` for a tradeable sleeve. Does NOT mean "model sleeve". */
const NORMAL_SLEEVE_TYPE = 'Normal sleeve';

/** The only `targetBalanceType` whose allocations are percentages. */
const PERCENT_BALANCE_TYPE = 'Percent';

/** Below this, a search is too broad to be useful and just loads the security master. */
const SECURITY_SEARCH_MIN_CHARS = 2;

/** How many results to show once the ticker filter has been applied. */
const SECURITY_SEARCH_LIMIT = 25;

/**
 * Orion Eclipse `securityTypeId` → `securityType`, counted across the live security
 * master (23,031 records, sampled 2026-08-13):
 *
 * | id | type          | count |
 * | -- | ------------- | ----- |
 * |  1 | Mutual Fund   | 5,216 |
 * |  4 | CD            | 1,821 |
 * |  5 | Equity        | 9,641 |
 * |  6 | Bond          | 4,755 |
 * |  7 | Option        | 1,359 |
 * |  9 | Annuity       |   190 |
 * | 10 | Other         |    47 |
 * | 11 | Miscellaneous |     2 |
 *
 * ⚠️ Ids **2, 3 and 8 are unaccounted for** — absent from this snapshot, but they may
 * exist in the source system, so treat the list as observed rather than exhaustive.
 *
 * ⚠️ Note there is **no Cash type in the live data**, despite Orion's v1 spec showing
 * `Cash` / `CASH` examples. If it exists it is presumably one of the missing ids, which
 * is why the name-based fallback below still names it.
 */
/**
 * Every `securityTypeId` Orion currently reports as active, from the full list AndyP pulled on
 * 2026-09-11. Nothing is decided by this set -- it exists so an id we have never seen can be told
 * apart from one we deliberately do not permit, and reported rather than dropped in silence.
 *
 * ⚠️ Ids 3 and 8 are absent from both this list and the 23,031-record sample, so they are
 * presumably retired or never used. If Orion adds an id, it lands here as unrecognised and warns.
 */
const KNOWN_SECURITY_TYPE_IDS = new Set([1, 2, 4, 5, 6, 7, 9, 10, 11]);

/**
 * The same set by NAME, for rows Orion sends without an id.
 *
 * Used only to decide whether a refusal was DELIBERATE or a thing we have never seen -- a row
 * named "Bond" is refused on purpose and needs no warning, where one named "Structured Note" or
 * nothing at all is a refusal nobody decided.
 */
const KNOWN_SECURITY_TYPE_NAMES = new Set([
  'mutual fund', 'cash', 'cd', 'equity', 'bond', 'option', 'annuity', 'other', 'miscellaneous',
]);

/**
 * What the advisor may BUY, by type id: Mutual Fund (1), CASH (2), Equity (5).
 *
 * ⚠️ **The same three ids as {@link ALLOCATABLE_SALE_TYPE_IDS}, and that is the point rather than
 * a coincidence.** One rule now runs the length of a request: money the firm can commit inside a
 * single request is money in these three types, so a sale outside them is held back and a
 * purchase outside them cannot be made at all. They stay two sets because they answer about two
 * different Orion models -- a security-list row versus an account holding -- not because they are
 * allowed to differ. If one moves and the other does not, that is a bug, not a configuration.
 *
 * Restored on 2026-09-15 at AndyP's request, having been paused since 2026-09-11 ("let's not
 * restrict buys for right now, we might want to later but we need to check on a few things
 * first"). Two objections were live when the rule was first written. Both are recorded here with
 * what answered them, because the first looks like an oversight until you know why it is not:
 *
 *   - It **drops Bond and CD** -- together 6,576 of the 23,031-record security master, 28.6% of
 *     it -- and reverses reasoning this file used to carry ("an exclusion list deliberately,
 *     rather than allowing only Equity ... bonds and CDs are kept on the same reasoning").
 *
 *     🟢 **DELIBERATE, and it is not the size of the count that settles it.** Most of those
 *     6,576 records are not legitimately selectable: the master carries instruments that are
 *     matured, date-limited or otherwise not purchasable, and a picker that lists them invites
 *     an advisor to choose one that cannot be filled. Restoring the ids to "fix" the 28.6%
 *     would be restoring mostly noise.
 *
 *     🟢 **The sanctioned route is the MANUAL CUSIP entry** (ACMTECH-975), on the trade team's
 *     advice: an advisor buying a bond or a CD generally knows its CUSIP, and entering it puts
 *     the instrument in front of someone who can verify it is actually purchasable -- which a
 *     search result never was. So the request is not blocked; it takes the path with a human in
 *     it. That is also why the buy picker's manual option exists at all, and why removing it
 *     would turn this allow-list into a hard block.
 *
 *   - It **fails closed**, structurally the same shape as the asset-category rule that had to be
 *     removed after one unrecognised value ("M" for Money Market) locked an entire account. A
 *     type Orion adds tomorrow silently vanishes from the picker. **Not retired.** The
 *     mitigation is {@link warnOnRefusedSecurityTypes} in `searchSecurities`, which is wired
 *     and, now that rows are actually being refused, finally has something to report.
 *
 * 🔴 **CASH (2) IS STILL LISTED HERE AND IS NO LONGER OFFERED.** Read that twice before editing
 * this set. The picker refuses cash through {@link NON_BUYABLE_SECURITY_TYPE_IDS}, a separate
 * exclusion applied ahead of this list — so 2 belonging to "types this workflow understands" and
 * cash not being purchasable are now two facts, held in two places, each reversible without the
 * other. That was this set's own standing instruction, written when cash went in.
 *
 * Its history, because the same assertion has now meant three different things: cash originally
 * reached the picker by accident (the old exclusion list omitted 2, and the id branch returns
 * before the name list that DID exclude 'cash' ever runs); AndyP asked for it explicitly on
 * 2026-09-15, making it a decision; and he withdrew it on 2026-10-01, leaving equity and mutual
 * funds. **Do not "simplify" this by deleting 2 and 'cash' from these sets** — that re-couples
 * the two rules and loses the escape hatch below.
 *
 * ⚠️ **An EMPTY set means UNRESTRICTED, not "nothing allowed"** -- see
 * {@link isBuyableSecurityType}, which short-circuits on one. That inversion is the sharp edge
 * here and it survives the restore on purpose: it is what makes pausing the rule a one-line edit
 * rather than a revert, and it has now been used once. No spec can reach it while this set is
 * populated, so this comment is the only thing guarding it.
 */
const BUYABLE_SECURITY_TYPE_IDS = new Set([1, 2, 5]);

/**
 * Types whose sale proceeds can be spent again inside the SAME request: Mutual Fund, CASH, Equity.
 *
 * ⚠️ **THIS NO LONGER DECIDES WHAT MAY BE SOLD.** Every security type is sellable now. What this
 * set decides is where the money GOES: a sale of anything outside it is held as cash and set
 * aside, because the firm cannot commit those proceeds to a model or a purchase within one
 * request. See {@link requiresSellReview}.
 *
 * It was `SELLABLE_SECURITY_TYPE_IDS` and refused the sale outright, which was the wrong shape of
 * answer -- an advisor holding a bond can perfectly well liquidate it, and locking the checkbox
 * told them they could not. The restriction is on what the proceeds may FUND, not on the trade.
 *
 * 🟢 CASH is here and is belt-and-braces: `loadAll` splits `isCash` rows into `cashPositions`
 * before step 1 sees them, so cash never reaches this list at all.
 */
const ALLOCATABLE_SALE_TYPE_IDS = new Set([1, 2, 5]);

/**
 * Name fallbacks for rows whose `securityTypeId` is missing, matched case-insensitively —
 * Orion mixes `MUTUAL FUND` with `Mutual Fund` and `CASH` with `Cash`.
 *
 * ⚠️ Kept in step with the id sets above by hand. They are separate because Orion sends one or
 * the other and never guarantees both, not because they are allowed to differ.
 */
const BUYABLE_SECURITY_TYPES = new Set(['mutual fund', 'cash', 'equity']);
const ALLOCATABLE_SALE_TYPES = new Set(['mutual fund', 'cash', 'equity']);

/**
 * Types the buy picker will not offer, whatever the allow-list says — CASH (AndyP, 2026-10-01).
 *
 * 🔴 THIS IS WHY CASH IS STILL ON THE ALLOW-LIST ABOVE. Withdrawing it by deleting 2 and 'cash'
 * from {@link BUYABLE_SECURITY_TYPE_IDS} would have been the smaller diff and the wrong shape:
 * that set answers "what kinds of instrument does this workflow understand", and cash's presence
 * there was never in dispute. What changed is a separate decision — whether buying cash is
 * something the picker should OFFER. Both the allow-list and its spec said in as many words that
 * a withdrawal must come through a dedicated exclusion so the two stay independently reversible,
 * and this is that exclusion. Cash is restored by emptying these two sets, with nothing else
 * touched.
 *
 * ⚠️ APPLIED BEFORE the allow-list's empty-set escape hatch, deliberately. Pausing the type rule
 * (see {@link BUYABLE_SECURITY_TYPE_IDS}) must not quietly put cash back in the picker — that is
 * precisely the coupling this shape exists to prevent.
 *
 * 🟢 No new warning noise: {@link warnOnRefusedSecurityTypes} reports only ids missing from
 * `KNOWN_SECURITY_TYPE_IDS`, and 2 is known. A refused cash row is a decision, not a surprise.
 */
const NON_BUYABLE_SECURITY_TYPE_IDS = new Set([2]);
const NON_BUYABLE_SECURITY_TYPES = new Set(['cash']);

/**
 * OSI option symbol: root, optional space, 6-digit expiry, C or P, 8-digit strike —/**
 * OSI option symbol: root, optional space, 6-digit expiry, C or P, 8-digit strike —
 * `AAPL 240419C00200000`.
 *
 * A backstop for the type filter above, because `""` is a documented `securityType`
 * value, so an untyped contract would otherwise slip through. No real equity or fund
 * ticker has this shape, so the false-positive risk is negligible.
 */
const OSI_OPTION_SYMBOL = /^[A-Z.]{1,6} ?\d{6}[CP]\d{8}$/;

/**
 * How many to ask Orion for, before ranking.
 *
 * Deliberately far more than we display, and the reason CHANGED when name matches stopped
 * being discarded. It used to be headroom: Orion matches the description as well as the
 * symbol, so asking for 25 and keeping only ticker matches could return nothing even when a
 * real ticker match existed, because it sat at position 40 behind a run of name matches.
 *
 * ⚠️ Now that nothing is discarded for matching on the name, this is a CEILING rather than
 * headroom -- a security Orion ranks past 200 cannot be surfaced however well it matches. Kept
 * at 200 rather than raised on a guess: it is comfortably more than the 25 shown, and raising
 * it trades a bigger payload on every keystroke for results nobody scrolls to.
 */
const SECURITY_SEARCH_FETCH_LIMIT = 200;

/**
 * A load failure whose own `message` is fit to show the advisor.
 *
 * The store prints `message` verbatim for these and falls back to a generic
 * "please try again" for anything else, so only raise one when the text names
 * something specific. Extending this base is what opts a new error in.
 */
export abstract class ReallocationV2LoadError extends Error {}

/**
 * ⚠️ There is deliberately NO error for "the account is already in an SMA".
 *
 * There was one, and it refused the request at load: an SMA is the whole account, so the
 * reasoning went that there is no partial reallocation of one to build and every step would
 * offer choices that could not be filed.
 *
 * That has been reversed -- the advisor is now let into the request on an SMA account, and the
 * SMA workflow is being reworked around what they can do once they are there. Anything that
 * needs to constrain an SMA account belongs at the point of the constraint, not at the door.
 *
 * ⚠️ So do NOT reinstate a load-time throw as "the obvious fix" for whatever turns up next. The
 * mechanisms for selecting an SMA mid-request (`smaSelectionCheck`, `commitToSma`, the
 * `IsSMA` flag on the filed request) are untouched and are where that behaviour lives.
 */

/**
 * Thrown when the account carries a model that cannot be identified.
 *
 * Only reachable for an account holding its model at ACCOUNT level: a sleeved account's models
 * come from Connect with an agg id attached, and an unmatched sleeve is left for the advisor to
 * resolve on the row. Here there is no row to resolve — the wizard would either open pretending
 * the account holds nothing, or pre-select a guess.
 *
 * ⚠️ Blocking rather than warning stands on its own reasoning, and used to have a second: it
 * kept the load-time SMA check airtight, since an unidentifiable model cannot be tested for
 * `isSma`. That check is gone, so only the first reason remains -- which is sufficient. There
 * is no row for the advisor to fix here, so opening would mean opening on a false premise.
 */
export class AccountModelUnresolvedError extends ReallocationV2LoadError {
  constructor(modelName: string) {
    super(
      `This account holds a model this wizard cannot identify (${modelName}). Contact the trade `
      + 'team.',
    );
    this.name = 'AccountModelUnresolvedError';
  }
}

/** Thrown when the account isn't in Eclipse at all. */
export class AccountPositionsNotFoundError extends ReallocationV2LoadError {
  constructor() {
    super('The account or any of its positions could not be found.');
    this.name = 'AccountPositionsNotFoundError';
  }
}

/**
 * Thrown when no sleeve row carries a `portfolioId`.
 *
 * Every Eclipse account belongs to a portfolio and every sleeve row of an account
 * reports the same `portfolioId`, so a null across all of them is a data problem
 * rather than a shape to accommodate. It is raised instead of loading, because the
 * portfolio id gates the positions, the portfolio context AND the sleeve
 * allocations - see `loadCustomHoldingsPositions`.
 */
export class AccountPortfolioMissingError extends ReallocationV2LoadError {
  constructor() {
    super(
      'Orion did not report a portfolio for this account, so its positions and model '
      + 'allocations cannot be loaded. Please report this account - it cannot be '
      + 'reallocated until its portfolio is set in Orion.',
    );
    this.name = 'AccountPortfolioMissingError';
  }
}

/**
 * Thrown when the delivery method / frequency lookups could not be read.
 *
 * 🔴 Fatal on purpose, and only for the request types that have a funding step.
 * `DeliveryMethodID` and `DeliveryFrequencyID` are NOT NULL foreign keys on
 * `req.Contribution` / `req.Distribution`, so there is no version of this request that can be
 * filed without real ids. The only alternative to stopping is guessing, and a guessed id files
 * the request against a delivery method the advisor never chose — a wrong answer that a
 * succeeding POST would hide.
 *
 * Also raised when the lists come back EMPTY, which is not a different situation: these are seed
 * rows that are always present, so nothing coming back means the query or the environment is
 * wrong rather than that the firm offers no way to move money.
 */
export class DeliveryOptionsUnavailableError extends ReallocationV2LoadError {
  constructor() {
    super(
      'The delivery methods for this request could not be loaded, so it cannot be submitted. '
      + 'Please close this request and try again.',
    );
    this.name = 'DeliveryOptionsUnavailableError';
  }
}

const NO_CUSTOM_HOLDINGS_SLEEVE_NOTICE =
  'This account has no Custom Holdings sleeve, so there are no hand-traded positions to reallocate. '
  + 'Continue to choose model allocations, or close this request if that is not what you intended.';

const EMPTY_CUSTOM_HOLDINGS_SLEEVE_NOTICE =
  'This account\'s Custom Holdings sleeve holds no positions. Continue to choose model allocations.';

/**
 * 🔴 THE SLEEVE SAYS IT IS WORTH SOMETHING AND ITS HOLDINGS CAME BACK EMPTY. That is not a state
 * an account can actually be in, so it is reported as something to come back to rather than as a
 * fact about the account.
 *
 * Eclipse has been observed returning `[]` for a sleeve's holdings while the trade team runs bulk
 * updates, recovering on its own once the process finishes (AndyP, 2026-09-25).
 *
 * ⚠️ WHAT MAKES IT SAFE TO SAY IS THAT THE TWO FIGURES COME FROM DIFFERENT CALLS.
 * `getAccountDetails` supplies every sleeve's `accountValue.totalValue`; `getPortfolioHoldings`
 * supplies the holdings. One reporting a balance while the other reports nothing is a
 * contradiction between two independent sources, not a stale read of one.
 *
 * 🔴 NARROW ON PURPOSE -- EMPTY ONLY, NEVER PARTIAL (AndyP, 2026-09-25). A short holdings list is
 * the more dangerous version of this bug, but it cannot be detected here: `marketValue` takes
 * `pendingValue`, so a sleeve's holdings are NOT expected to sum to its reported total even when
 * everything is healthy, and any partial check would need a tolerance nobody has evidence for.
 * Zero against a non-zero balance needs no tolerance at all, whatever the valuation basis.
 */
const CUSTOM_HOLDINGS_UNAVAILABLE_NOTICE =
  'This account includes a Custom Holdings sleeve, however there are no holdings currently '
  + 'showing from our vendor. If this is incorrect, please try the request again in a few '
  + 'minutes, once our vendor account data has refreshed. We\'re sorry for any inconvenience.';

const EMPTY_ACCOUNT_NOTICE =
  'This account holds no positions to reallocate. Continue to choose model allocations.';

/**
 * Data seam for the Reallocation workflow (V2).
 *
 * Everything is real when an account is supplied:
 *  - account identity from the passed-in account,
 *  - positions from the account's **Custom Holdings sleeve** — each sleeve is its
 *    own Orion account, so `getAccountDetails` is what picks the right one and
 *    the portfolio-scoped `getPortfolioHoldings`, filtered to the sleeve's rows,
 *  - portfolio-level context (trade block, set-aside cash) from `getPortfolio`,
 *  - the model list from `InvestmentModelsClient.getAgentInvestmentModels`.
 *
 * A missing account propagates as `AccountPositionsNotFoundError` so the store can
 * show the not-found state. An account that simply has nothing to sell does NOT —
 * it resolves with an empty position list and a `positionsNotice`.
 *
 * With no account (unit tests / rendered without a selection) it returns the
 * static demo payload.
 */
@Injectable()
export class ReallocationV2DataService {
  private readonly modelsClient = inject(InvestmentModelsClient);
  private readonly eclipseClient = inject(OrionEclipseClient);
  private readonly connectClient = inject(OrionConnectClient);
  /** Standing bank authorizations come from Schwab live, not from a CRM table. */
  private readonly schwabClient = inject(SchwabClient);
  /** The `[req]` read side, which also serves the delivery method/frequency lookups. */
  private readonly accountRequestsClient = inject(AccountRequestsClient);

  /**
   * Which wave of the load is in flight, for the wizard's progress display. Set at the
   * real wave boundaries rather than on a timer, so it never runs ahead of the requests.
   */
  readonly loadStage = signal<ReallocationV2LoadStage>('account');

  /** Load the account, positions and models for the workflow's account. */
  loadFormData(
    account: AccountModel | undefined,
    funding?: FundingLoadOptions,
  ): Observable<ReallocationV2Data> {
    // No account (tests / rendered without a selection): static demo payload. Resolves
    // synchronously, so report it done rather than flashing a progress list.
    if (!account) {
      this.loadStage.set('done');
      return of(MOCK_REALLOCATION_V2_DATA);
    }

    this.loadStage.set('account');
    const agentIds = account.advisors?.map((a) => a.agentID) ?? [];
    return forkJoin({
      models: this.loadModels(agentIds),
      allModels: this.loadAllModels(),
      sleeves: this.loadSleeves(account),
      // Firm-wide reference data, independent of the account, so it rides along
      // with the first round trip rather than adding one.
      modelAggs: this.loadModelAggs(),
      eclipseModels: this.loadEclipseModels(),
      // Only for the request types that have a funding step. `undefined` here is what makes
      // `data.funding` absent for types 1 and 2, which is in turn how the store knows the wave
      // was not run rather than that it came back empty.
      funding: funding ? this.loadFunding(account, funding) : of(undefined),
    }).pipe(
      tap(() => this.loadStage.set('positions')),
      switchMap(({ models, allModels, sleeves, modelAggs, eclipseModels, funding: fundingOptions }) => {
        // Resolved once and threaded through: both the positions call and the
        // empty-step-1 notice have to agree on where positions come from.
        const source = findPositionSource(sleeves);
        return forkJoin({
          positions: this.loadCustomHoldingsPositions(account, sleeves, source.row),
          portfolio: this.loadPortfolio(account, sleeves),
        }).pipe(
          tap(() => this.loadStage.set('sleeves')),
          switchMap(({ positions, portfolio }) =>
            // Needs the registrationId the portfolio call supplies, so it cannot
            // join the round trip above.
            this.loadSleeveAllocations(account, portfolio).pipe(
              map((sleeveAllocations) => ({ models, allModels, sleeves, source, positions, portfolio, modelAggs, eclipseModels, sleeveAllocations, fundingOptions })),
            ),
          ),
        );
      }),
      map(({ models, allModels, sleeves, source, positions, portfolio, modelAggs, eclipseModels, sleeveAllocations, fundingOptions }) => {
        // Throws AccountModelUnresolvedError when the account holds something this wizard
        // cannot identify -- there is no row for the advisor to fix in that case, so opening
        // would mean opening on a false premise.
        // ⚠️ RE-VALUED FROM ECLIPSE before anything reads a dollar off it (ACMTECH-973). Connect
        // supplies the sleeve's configuration; Eclipse supplies what it is worth. One list from
        // here down, so nothing can pick up the pre-swap figures by accident.
        const valuedSleeves = withEclipseSleeveValues(sleeveAllocations, sleeves, account);

        const accountLevelSleeve = resolveAccountLevelSleeve(
          sleeves, valuedSleeves, modelAggs, eclipseModels, models, allModels,
        );

        // Split once, into two disjoint lists, and named so the two rules below cannot be
        // confused. ⚠️ `positions` at this point is the RAW list and still holds the cash rows,
        // which is why `noticeFor` used to read as cash-aware without being so on purpose.
        //
        // Cash is not a SELLABLE position -- it has no shares and no price -- but since it
        // became opt-in on step 1 it is very much a position the advisor acts on, so it gets its
        // own list rather than being dropped.
        const sellablePositions = orderForStepOne(positions.filter((p) => !p.isCash));
        // Alphabetical too, by the same comparator. Usually one or two rows, so it rarely shows
        // -- but an account with several cash-like holdings should not present them in one order
        // here and another on step 1.
        const cashRows = positions.filter((p) => p.isCash).sort(byTicker);

        // ⚠️ A DIAGNOSTIC, not a gate. See `warnIfPartsDoNotTie` -- the account is still
        // returned and the request still works, because a reporting difference between two
        // Orion products is not a reason to take the screen away from an advisor.
        //
        // Built once and shared, so the check measures the SAME total the advisor is shown
        // rather than recomputing one that could drift from it.
        const accountInfo = this.buildAccountInfo(account, sleeves);
        // ⚠️ Measured against the list that actually reaches the store, not the raw Connect one --
        // otherwise this would go on reporting the gap the re-valuing above just closed.
        warnIfPartsDoNotTie(
          accountInfo, sellablePositions, cashRows,
          accountLevelSleeve ? [accountLevelSleeve] : valuedSleeves,
        );

        return {
          account: accountInfo,
          positions: sellablePositions,
          cashPositions: cashRows,
          models,
          allModels,
          portfolio,
          positionsNotice: noticeFor(source, sellablePositions, cashRows),
          // ⚠️ The NOTICE alone cannot be branched on -- it is prose, and matching it would
          // break the next time a word changes. This says which case produced it.
          customHoldingsUnavailable:
            sellablePositions.length === 0
            && cashRows.length === 0
            && customHoldingsLooksUnavailable(source),
          // ⚠️ The synthetic sleeve REPLACES Connect's list rather than joining it, and it can
          // only exist when that list was empty (see `resolveAccountLevelSleeve`) -- so this is
          // never both. Concatenating would have been the same thing today and a double count
          // the moment Connect started reporting sleeves for these accounts.
          sleeves: accountLevelSleeve ? [accountLevelSleeve] : valuedSleeves,
          modelMinimums: buildModelMinimums(modelAggs, valuedSleeves, allModels),
          funding: fundingOptions,
        };
      }),
      tap(() => this.loadStage.set('done')),
    );
  }

  /**
   * Real account identity plus the account total.
   *
   * The total is the sum of every sleeve's `accountValue.totalValue`, which Orion
   * reports on the **pending** basis — the same basis its own screens, the portfolio
   * summary and Orion Connect all use. Summing the position rows' `value` instead
   * would give a market-basis figure no Orion surface reports.
   *
   * Deliberately account-wide, not Custom-Holdings-only: the wizard's `% of account`
   * column and its investable arithmetic are relative to the whole account, while the
   * positions listed are only the hand-traded sleeve's.
   */
  /**
   * Everything the funding step binds to: the two `[req]` lookups, the account's bank
   * authorizations, and the withholding percentages currently on file.
   */
  private loadFunding(account: AccountModel, options: FundingLoadOptions): Observable<FundingOptions> {
    return forkJoin({
      lookups: this.loadDeliveryOptions(),
      instructions: this.loadStandingInstructions(account, options.direction),
    }).pipe(
      map(({ lookups, instructions }) => ({
        deliveryMethods: lookups.deliveryMethods,
        deliveryFrequencies: lookups.deliveryFrequencies,
        standingInstructions: instructions.options,
        standingInstructionsFailed: instructions.failed,
        // Straight off the account the modal already loaded — there is no separate withholding
        // endpoint, and V1 reads the same two fields.
        federalTaxWithholding: account.federalTaxWithholding ?? null,
        stateTaxWithholding: account.stateTaxWithholding ?? null,
      })),
    );
  }

  /**
   * The `req.DeliveryMethod` / `req.DeliveryFrequency` lookups.
   *
   * 🔴 **A failure here is FATAL for these request types, and there is no fallback by design.**
   * `DeliveryMethodID` and `DeliveryFrequencyID` are NOT NULL foreign keys on
   * `req.Contribution` / `req.Distribution`, so a request cannot be filed without real ids —
   * and the only alternative to failing is guessing, which would file against a delivery method
   * nobody chose. An empty dropdown would also read as "no options exist", which is the
   * ACMTECH-935 mistake: asserting a "no" that was never established.
   *
   * ⚠️ So this deliberately does NOT `catchError` to an empty list, unlike almost every other
   * loader in this service. The error propagates out of the funding wave, out of
   * `loadFormData`, and lands on the store's `loadError` panel.
   *
   * The three flags are taken as sent and never re-derived here — each is decided once, in the
   * FIG Data query, so the ACH-only rule and the transaction-type mapping have exactly one home.
   */
  private loadDeliveryOptions(): Observable<{
    deliveryMethods: DeliveryMethodOption[];
    deliveryFrequencies: DeliveryFrequencyOption[];
  }> {
    return this.accountRequestsClient.getDeliveryOptions().pipe(
      map((options) => ({
        deliveryMethods: (options?.methods ?? []).map((m) => ({
          id: m.id,
          name: m.name ?? '',
          // An identity, not a policy: whether the method may be CHOSEN depends on the request
          // type and is decided in the store (see `deliveryMethodLocked`). `=== true` rather
          // than a cast because the generated type is non-nullable but the wire is not.
          isAch: m.isAch === true,
          transactionType: m.transactionType ?? '',
        })),
        deliveryFrequencies: (options?.frequencies ?? []).map((f) => ({
          id: f.id,
          name: f.name ?? '',
          oneTime: f.isOneTime === true,
        })),
      })),
      map((options) => {
        // An empty list is treated as a failure too. The lookups are seed data that is always
        // present, so "nothing came back" means the query or the environment is wrong rather
        // than that the firm offers no delivery methods -- and the advisor's next action is the
        // same as for an outright error.
        if (options.deliveryMethods.length === 0 || options.deliveryFrequencies.length === 0) {
          throw new DeliveryOptionsUnavailableError();
        }
        return options;
      }),
      // ⚠️ NOT a fallback -- it re-throws. The raw error is replaced with the typed one so the
      // advisor is told what actually failed: the store renders `err.message` only for a
      // ReallocationV2LoadError, so an HttpErrorResponse arriving here would surface as the
      // generic "there was an error loading the form" and hide a specific, actionable cause.
      catchError((err) =>
        throwError(() =>
          err instanceof DeliveryOptionsUnavailableError ? err : new DeliveryOptionsUnavailableError(),
        ),
      ),
    );
  }

  /**
   * The account's standing bank authorizations, filtered to the direction the money moves.
   *
   * Schwab-only, and guarded on both Schwab identifiers exactly as the V1 workflows are: an
   * account held elsewhere has no standing instructions to fetch, which is not a failure.
   *
   * 🔴 Reports failure separately from emptiness. "No authorization on file" tells the advisor
   * to file custodial paperwork; "the lookup failed" tells them to try again. Collapsing the
   * two into an empty list asserts a "no" that was never established — the same mistake
   * ACMTECH-935 cost a card for.
   */
  private loadStandingInstructions(
    account: AccountModel,
    direction: 'INCOMING' | 'OUTGOING',
  ): Observable<{ options: StandingInstructionOption[]; failed: boolean }> {
    // Always offered, and always last: choosing it is how the advisor says the money is going
    // somewhere the account has no authorization for yet. `hasIaAuthority: false` is a fact
    // about it rather than a missing value, so it is stated rather than left null.
    const other: StandingInstructionOption = {
      key: OTHER_STANDING_INSTRUCTION_KEY,
      displayValue: OTHER_STANDING_INSTRUCTION_KEY,
      transactionType: 'ALL',
      hasIaAuthority: false,
    };

    if (!account.schwabMasterAccount || !account.schwabFormattedAccount) {
      return of({ options: [other], failed: false });
    }

    return this.schwabClient
      .getAccountStandingInstructions(
        account.investmentAccountID,
        account.schwabMasterAccount,
        account.schwabFormattedAccount,
      )
      .pipe(
        map((results) => ({
          options: [
            ...(results ?? [])
              .filter((r) => r.direction === direction)
              .map((r) => ({
                key: r.displayValue ?? '',
                displayValue: r.displayValue ?? '',
                transactionType: r.transactionType ?? '',
                // Left null when Schwab did not say. See StandingInstructionOption.
                hasIaAuthority: r.hasIaAuthority ?? null,
              }))
              // A blank displayValue cannot be keyed on or stored, and DeliveryInstruction is
              // what the trade team reads to know where the money goes.
              .filter((o) => o.key !== ''),
            other,
          ],
          failed: false,
        })),
        catchError(() => {
          console.warn('[reallocation-v2] Standing authorizations could not be loaded.');
          return of({ options: [other], failed: true });
        }),
      );
  }

  private buildAccountInfo(account: AccountModel, sleeves: OrionEclipseAccountDetailModel[]): AccountInfo {
    return {
      custodian: account.custodian ?? '',
      accountNumber: account.accountNumber ?? '',
      clientName: deriveClientName(account),
      totalValue: sleeves.reduce((sum, s) => sum + (s.accountValue?.totalValue ?? 0), 0),
    };
  }

  /**
   * The account's Eclipse rows: the custodial parent plus one per sleeve.
   *
   * Hard-errors only when Eclipse knows nothing about the account at all — every
   * other shortfall (no Custom Holdings sleeve, an empty one) is a notice, not an
   * error, and is decided further down.
   */
  private loadSleeves(account: AccountModel): Observable<OrionEclipseAccountDetailModel[]> {
    return this.eclipseClient.getAccountDetails(account.investmentAccountID).pipe(
      map((details) => {
        if (!details?.length) throw new AccountPositionsNotFoundError();
        return details;
      }),
    );
  }

  /**
   * Holdings of the Custom Holdings sleeve, mapped to positions.
   *
   * Sourced from the **portfolio**-scoped endpoint, not the account-scoped one, even
   * though the account-scoped call is already sleeve-correct once given the right
   * sleeve's id. The two share a response model but not its population: the
   * account-scoped call returns `costAmount` and all three `unrealizedGainLoss` fields
   * as null on real securities, which would blank step 1's Total Cost and Unrealized
   * G/L columns — on the screen whose whole purpose is choosing what to sell.
   *
   * One call therefore covers three things: cost basis, an account-relative
   * `currentInPer`, and per-sleeve attribution via each row's `accountId`.
   *
   * "Across all sleeves" is not a problem — it is a filter. A row's `accountId` **is**
   * the Eclipse account id of its sleeve, confirmed against the ids `getAccountDetails`
   * reports for the same account.
   *
   * There is deliberately NO fallback to the account-scoped call. A missing portfolio id
   * raises AccountPortfolioMissingError instead - see the note in the body.
   */
  private loadCustomHoldingsPositions(
    account: AccountModel,
    sleeves: OrionEclipseAccountDetailModel[],
    sleeve: OrionEclipseAccountDetailModel | undefined,
  ): Observable<Position[]> {
    if (!sleeve) return of([]);

    const accountTotal = sleeves.reduce((sum, s) => sum + (s.accountValue?.totalValue ?? 0), 0);
    const portfolioId = sleeve.portfolioId ?? sleeves.find((s) => s.portfolioId != null)?.portfolioId;

    // No fallback to the account-scoped endpoint, by decision. Every Eclipse account
    // belongs to a portfolio and every sleeve row of an account reports the same
    // portfolioId, so a null across all of them is a data error rather than a shape to
    // route around - and falling back HID it: positions still loaded (without cost basis
    // or gain/loss) while loadPortfolio returned null and loadSleeveAllocations returned
    // [], because registrationId comes from the portfolio. The result was a half-loaded
    // wizard that looked complete - step 1 populated, every sleeve allocation missing.
    if (portfolioId == null) throw new AccountPortfolioMissingError();

    return this.eclipseClient.getPortfolioHoldings(portfolioId, account.investmentAccountID).pipe(
      map((holdings) => toPositions((holdings ?? []).filter((h) => h.accountId === sleeve.id), accountTotal)),
    );
  }

  /**
   * Portfolio-level context. All sleeves share one portfolio, so the id is taken from
   * whichever row carries one.
   *
   * Non-fatal by design: nothing in the wizard depends on it yet (it is slice 7's
   * input), so a failure here must not take the whole load down with it.
   */
  private loadPortfolio(
    account: AccountModel,
    sleeves: OrionEclipseAccountDetailModel[],
  ): Observable<PortfolioContext | null> {
    const portfolioId = sleeves.find((s) => s.portfolioId != null)?.portfolioId;
    if (portfolioId == null) return of(null);

    return this.eclipseClient.getPortfolio(portfolioId, account.investmentAccountID).pipe(
      map((portfolio) => toPortfolioContext(portfolioId, portfolio)),
      catchError(() => of(null)),
    );
  }

  /**
   * The account's existing model sleeves, for step 4's pre-load.
   *
   * Sleeving lives at the **registration**, not the account — `Trading/Sleeve/Accounts/{id}`
   * returns "Object does not exist" for a custodial parent — and the registration id
   * arrives free on the portfolio detail, so this costs no id-discovery call.
   *
   * Non-fatal: an account with no registration, or a failed lookup, pre-loads nothing
   * and step 4 starts at a single blank row exactly as it did before.
   */
  private loadSleeveAllocations(
    account: AccountModel,
    portfolio: PortfolioContext | null,
  ): Observable<SleeveAllocation[]> {
    const registrationId = portfolio?.registrationId;
    if (registrationId == null) {
      // The one path here that used to say NOTHING. A non-percent sleeve and an unmatched model
      // both warn, so a blank allocation grid always had a reason in the console -- except this
      // one, which cost an afternoon of looking for a bug in the grid.
      //
      // Not an error: an account with no registration may legitimately hold its model at account
      // level, which resolveAccountLevelSleeve now picks up.
      // eslint-disable-next-line no-console
      console.warn(
        `[reallocation-v2] Account ${account.accountNumber ?? account.investmentAccountID} has no `
        + 'Orion Connect registration, so no model sleeves were loaded.',
      );
      return of([]);
    }

    return this.connectClient.getSleeveRegistration(registrationId, account.investmentAccountID).pipe(
      map((registration) => toSleeveAllocations(registration?.accountSleeves)),
      catchError(() => of([])),
    );
  }

  /**
   * Orion Connect's model aggregates — firm-wide reference data carrying each model's
   * `minimumInvestmentAmount`. Supplies the minimum for a model the advisor **adds**,
   * which has no sleeve to read one from.
   *
   * Returns null (not an empty list) on failure rather than throwing: blocking every allocation
   * because a reference lookup was down would be the wrong failure.
   *
   * ⚠️ The null used to also carry MEANING -- it let the store tell "this model has no minimum
   * on record" from "the minimums are unavailable", which were different answers back when the
   * first one blocked Next. Neither blocks now (see `ReallocationV2Store.minimumFor`), so the
   * distinction survives only in the diagnostics. Not worth collapsing, but do not build on it.
   */
  private loadModelAggs(): Observable<OrionConnectTradingModelAggModel[] | null> {
    return this.connectClient.getTradingModelAggs().pipe(
      map((aggs) => aggs ?? []),
      catchError(() => of(null)),
    );
  }

  /**
   * Eclipse's model list, firm-wide.
   *
   * Fetched for ONE reason: community models. Orion manages some models centrally and links
   * them between Eclipse and Connect by `CommunityModelId` rather than by `EclipseModelId`,
   * which is null on those aggs. The account-detail row carries an Eclipse `modelId` and
   * nothing else, so bridging the two needs this list -- see `resolveAccountLevelSleeve`.
   *
   * Returns null (not an empty list) on failure, for the same reason `loadModelAggs` does: the
   * community hop then misses and the name fallback carries what it can, rather than every
   * account being refused because a reference lookup was down.
   *
   * ⚠️ Rides in wave 1 with the other firm-wide reference data, so it adds no round trip to
   * the load. It is a whole-firm list and account-independent.
   */
  private loadEclipseModels(): Observable<OrionEclipseModelListResponseModel[] | null> {
    return this.eclipseClient.getModels().pipe(
      map((models) => models ?? []),
      catchError(() => of(null)),
    );
  }

  /**
   * Type-ahead over Eclipse's security master, for the Buy Additional Securities step.
   *
   * ⚠️ Deleted and inactive securities are filtered out — nothing stops Orion returning
   * them, and letting an advisor pick one produces a request the trade team cannot
   * action. Orion reports `isDeleted` as a numeric 0/1 despite the swagger declaring a
   * boolean, so compare explicitly rather than relying on truthiness.
   *
   * TODO(ACMTECH-893): per-security Do Not Buy belongs here — flag a restricted result
   * rather than hiding it. 893's interim inheritance rule errs toward *reporting* a
   * restriction, so excluding would silently drop securities an advisor may buy.
   * Restrictions are NOT limited to securities the account already holds: inherited
   * firm/custodian rows cover ones it has never held.
   *
   * ⚠️ **Matches the NAME as well as the ticker, and RANKS rather than filters.** An advisor
   * looking for Apple can type "apple" instead of knowing "AAPL".
   *
   * Name matches used to be thrown away here, on a real objection: typing "AA" came back with
   * everything from Alcoa to American Funds, which is noise when the advisor is reaching for a
   * symbol they already know. But the noise was that name matches DISPLACED ticker matches, and
   * ranking makes that impossible -- an exact ticker sorts first, ticker prefixes next, names
   * last, and the 25-row cap trims the tail rather than the head. Reverting to a filter would
   * bring back the "apple" problem to solve a problem the order already solves.
   *
   * Non-fatal: a failed search yields no results rather than breaking the step.
   */
  searchSecurities(term: string): Observable<SecurityOption[]> {
    const trimmed = term.trim();
    if (trimmed.length < SECURITY_SEARCH_MIN_CHARS) return of([]);
    const wanted = trimmed.toUpperCase();

    return this.eclipseClient.getSecurities(trimmed, SECURITY_SEARCH_FETCH_LIMIT).pipe(
      map((securities) => {
        const live = (securities ?? []).filter((s) => s.isDeleted !== 1 && s.id != null);
        // Reported once per search rather than per row: a keystroke can bring back 200 of these.
        // LIVE again as of 2026-09-15. It reports only types the allow-lists do not RECOGNISE, so
        // an ordinary refused Bond is silent while a type Orion adds tomorrow is not -- which is
        // the mitigation for an allow-list failing closed. That is how the old asset-category
        // rule reached a pilot advisor: a greyed row and nothing in the console.
        warnOnRefusedSecurityTypes(
          live.filter((s) => !isBuyableSecurityType(s))
            .map((s) => ({ id: s.securityTypeId, name: s.securityType })),
          `Security search "${trimmed}"`,
        );
        return live
          .filter((s) => isBuyableSecurityType(s))
          .filter((s) => !OSI_OPTION_SYMBOL.test((s.symbol ?? '').trim().toUpperCase()))
          .map((s) => ({ security: s, rank: matchRank(s, wanted) }))
          // Orion's own `search` is looser than this -- it matches a name ANYWHERE, so "apple"
          // also returns "Pineapple Growth Fund". A null rank is how those are dropped.
          .filter((scored) => scored.rank !== null)
          // ⚠️ STABLE within a rank, deliberately: no tie-breaker on ticker length or
          // alphabetically. Inside a band Orion's own relevance order is better information than
          // anything derivable from the symbol, and `Array.prototype.sort` has been required to
          // be stable since ES2019, so equal ranks keep the order they arrived in.
          .sort((a, b) => (a.rank as number) - (b.rank as number))
          .slice(0, SECURITY_SEARCH_LIMIT)
          .map((scored) => toSecurityOption(scored.security));
      }),
      catchError(() => of([])),
    );
  }

  /**
   * The models this advisor may **choose** — agent-scoped, Custom Holdings removed.
   * This is the dropdown's list, and it is the smaller of the two.
   */
  private loadModels(agentIds: number[]): Observable<ModelOption[]> {
    return this.modelsClient.getAgentInvestmentModels(agentIds).pipe(
      // Custom Holdings (id 181) is not a selectable model in this workflow, so
      // drop it from the list.
      // TODO(api): ideally the endpoint excludes Custom Holdings itself, but
      // other current requests using getAgentInvestmentModels still depend on it
      // being returned — so this must stay a client-side filter until those
      // callers are migrated.
      map((models) => models.filter((m) => m.id !== customHoldingsModelID).map(toModelOption)),
    );
  }

  /**
   * Every model in CRM, used only to **resolve** what an account already holds.
   *
   * Agent scoping is right for the dropdown and wrong here: a sleeve can sit on a model
   * this advisor is not offered, and that row still has to show which model it is.
   *
   * ⚠️ Custom Holdings is deliberately NOT filtered out of this list. Filtering the
   * resolution list would reintroduce the blank row it exists to prevent. It stays out
   * of the dropdown because that comes from {@link loadModels}.
   *
   * Non-fatal: on failure the pre-load falls back to the agent-scoped list, which is
   * how it behaved before this existed.
   */
  private loadAllModels(): Observable<ModelOption[]> {
    return this.modelsClient.getInvestmentModels().pipe(
      map((models) => (models ?? []).map(toModelOption)),
      catchError(() => of([])),
    );
  }
}

/**
 * Whether a security is the sort of thing this workflow can buy: Mutual Fund or Equity.
 *
 * Id first — it is stable, where `securityType` casing is not. The name is the fallback
 * for a row with no id, and matters because ids 2, 3 and 8 were unobserved: a security
 * carrying one of those arrives here unrecognised, and the name is the only other thing
 * to go on.
 *
 * ⚠️ This is NOT the only thing filtering the picker. `searchSecurities` also drops anything
 * whose symbol has the OSI option shape. That rule is now REDUNDANT for a typed contract -- 7 is
 * not on the allow-list -- and is kept because it is the only one that catches a contract Orion
 * sent with no type at all, which this predicate would otherwise judge on the name alone. The
 * two overlap deliberately; neither is a superset of the other.
 *
 * 🔴 AND IT DROPS ORION'S CUSTODIAL-CASH SECURITIES (AndyP, 2026-10-01), which is a SECOND cash
 * rule and not a duplicate of the type exclusion. `securityTypeId` 2 catches rows Orion TYPES as
 * CASH; `custodialCash` is a flag on the security master marking the custodian's own sweep
 * vehicles, and those arrive carrying ordinary types -- a sweep fund types as Mutual Fund (1),
 * which the allow-list admits. Neither rule sees what the other does.
 */
/**
 * Whether Orion flags this security as the custodian's own cash.
 *
 * ⚠️ PRESENT-AND-NON-ZERO, not `=== 1`. The documented value is 1, and matching it exactly would
 * silently admit any other truthy value Orion might start sending. Nothing is lost by the wider
 * test: 0 and a missing flag are the only ways to say "not custodial cash", and both still pass.
 *
 * 🟢 FAILS OPEN on `undefined`, deliberately, where the type rules fail closed. A missing
 * `custodialCash` is the ordinary shape of every non-cash security rather than a gap in the data,
 * so treating absence as "is cash" would empty the picker the moment Orion stopped sending the
 * field. The type allow-list is still in front of it.
 *
 * ⚠️ PICKER ONLY, and it cannot be otherwise. This reads the security-master row, which the
 * "Buy more" shortcut on a held position does not have -- that caller reaches
 * {@link isBuyableSecurity} with a type id and nothing else. The two rules therefore do NOT
 * agree here, which breaks the "one rule, two callers" property on purpose: a custodial-cash
 * holding is split into `cashPositions` by `loadAll` before step 1 renders, so no such row ever
 * carries a Buy more button to disagree with.
 */
function isCustodialCashSecurity(security: OrionEclipseSecurityListResponseModel): boolean {
  return security.custodialCash != null && security.custodialCash !== 0;
}

function isBuyableSecurityType(security: OrionEclipseSecurityListResponseModel): boolean {
  if (isCustodialCashSecurity(security)) return false;
  return isBuyableSecurity(security.securityTypeId, security.securityType);
}

/**
 * The buyable rule itself, reachable without a security-master row.
 *
 * ⚠️ EXPORTED so the "Buy more" action on a HELD position answers the same question the picker
 * does. That button offers a purchase; if it used its own rule, an advisor could add a row for
 * something the picker would refuse to find, and the disagreement would only surface at submit.
 * One rule, two callers.
 *
 * ⚠️ A NULL type is NOT buyable here, where an unrecognised search result falls back to the name
 * set. A holding carries no `securityType` string to fall back ON -- `assetCategoryName` is a
 * different vocabulary and matching against it would be guesswork -- so the honest answer is to
 * withhold the shortcut and let the advisor use the picker, which can still resolve it. Fails
 * closed, like `requiresSellReview`: the cost is an extra search, not a wrong trade.
 */
export function isBuyableSecurity(securityTypeId: number | null | undefined, securityType?: string | null): boolean {
  // 🔴 FIRST, and above the escape hatch below: an exclusion the allow-list cannot overrule, and
  // that pausing the allow-list cannot silently lift. See {@link NON_BUYABLE_SECURITY_TYPE_IDS}.
  if (isNonBuyableSecurityType(securityTypeId, securityType)) return false;

  // ⚠️ An EMPTY allow-list means the rule is switched OFF, not that nothing qualifies. Dead
  // while the sets above are populated, and kept anyway: it is the pause lever, used once
  // already (2026-09-11 to 2026-09-15). Without it, emptying the sets to disable the rule would
  // instead refuse everything and hand every advisor an empty picker. Both sets are checked
  // because a row carries one or the other, so leaving either populated would half-apply a rule
  // nobody asked for.
  if (BUYABLE_SECURITY_TYPE_IDS.size === 0 && BUYABLE_SECURITY_TYPES.size === 0) return true;

  if (securityTypeId != null) return BUYABLE_SECURITY_TYPE_IDS.has(securityTypeId);
  if (securityType == null) return false;
  return BUYABLE_SECURITY_TYPES.has(securityType.trim().toLowerCase());
}

/**
 * Whether the picker refuses this type outright.
 *
 * ⚠️ ID FIRST, mirroring {@link isBuyableSecurity} exactly. A typed row is judged on its id and
 * the name is never consulted, so a row carrying id 2 is refused whatever Orion called it, and a
 * row with no id at all falls back to the name — which is the only way the untyped `CASHX` style
 * of row is caught.
 *
 * 🟢 An empty pair means "exclude nothing", which is the ordinary `Set.has` answer rather than a
 * special case. No inversion here, unlike the allow-list above — that trap is worth having once,
 * not twice.
 */
function isNonBuyableSecurityType(
  securityTypeId: number | null | undefined,
  securityType?: string | null,
): boolean {
  if (securityTypeId != null) return NON_BUYABLE_SECURITY_TYPE_IDS.has(securityTypeId);
  if (securityType == null) return false;
  return NON_BUYABLE_SECURITY_TYPES.has(securityType.trim().toLowerCase());
}

/**
 * Whether a HOLDING may be sold through this workflow.
 *
 * ⚠️ There was no rule here at all: every position reaching step 1 was sellable, because the
 * asset-category allow-list was removed on 2026-09-04 after it locked an account whose money
 * market fund arrived as `assetCategoryName: "M"` instead of "Money Market". This replaces it on
 * `securityTypeId`, which is an id rather than a string Orion spells two ways.
 *
 * ⚠️ It does NOT block structured products, and nothing about type id can. Structured Note,
 * Structured CD and Structured Product exist only as asset CATEGORIES (SN, SCD, SP) -- a
 * Structured CD arrives as type 4 and is indistinguishable from an ordinary CD. Type 4 is not
 * permitted here, so structured CDs are excluded incidentally, but a structured product arriving
 * as Equity would not be. Blocking them specifically is a separate problem and needs a separate
 * field; see the block comment above `orderForStepOne`.
 *
 * The lock MECHANISM was left intact when the old rule went -- `Position.sellable`, the disabled
 * control in `buildPositionsForm`, the N/A cell, the `toggleSellAll` skip -- so this is one
 * predicate rather than a rebuild.
 */
function requiresSellReview(holding: OrionEclipseAccountHoldingsResponseModel): boolean {
  if (holding.securityTypeId != null) return !ALLOCATABLE_SALE_TYPE_IDS.has(holding.securityTypeId);

  // ⚠️ FAILS SAFE. An untyped row, or one whose name we do not recognise, requires review.
  //
  // The costs are not symmetric. Treat an unknown as ordinary and its proceeds enter the
  // investable pool and get spent -- so if it was in fact a bond or a structured product, money
  // that should have been held has already been committed. Treat it as reviewable and an
  // ordinary equity's proceeds sit as cash the advisor cannot use this time round: annoying,
  // and recoverable.
  //
  // ⚠️ AndyP's expectation (2026-09-14) is that every Custom Holdings row carries a type, which
  // would make this branch dead. It is NOT verified -- the generated DTO says `number | undefined`
  // for every numeric on that model including `securityId`, so the contract cannot settle it. The
  // warning below is what will tell us, and it is why this branch keeps a warning of its own
  // rather than silently taking the safe path forever.
  const name = (holding.securityTypeName ?? '').trim().toLowerCase();
  return !ALLOCATABLE_SALE_TYPES.has(name);
}

/**
 * Say so when something was refused for a reason nobody decided.
 *
 * ⚠️ The point of this, and the reason both filters can be allow-lists at all. An allow-list
 * fails closed, so a type Orion adds tomorrow silently disappears from the picker or quietly
 * becomes unsellable -- which is exactly how the old asset-category rule reached a pilot advisor:
 * a greyed checkbox and nothing in the console.
 *
 * ⚠️ TWO kinds of unknown, and missing the second was a real hole in the first version of this:
 *
 *   - an id outside {@link KNOWN_SECURITY_TYPE_IDS} -- Orion has added a type;
 *   - NO id at all, and a name we do not recognise either. The old exclusion list had a
 *     name-based fallback precisely because Orion does send untyped rows, and under an
 *     allow-list every one of those is refused. Reporting only ids would have made the
 *     commonest silent failure the one case that stayed silent.
 *
 * Deliberate refusals are NOT reported. Bond and CD are excluded on purpose, and saying so on
 * every keystroke would train everyone to ignore the channel.
 */
function warnOnRefusedSecurityTypes(
  refused: { id: number | null | undefined; name: string | null | undefined }[],
  context: string,
): void {
  const unknownIds = [...new Set(
    refused.map((r) => r.id).filter((id): id is number => id != null && !KNOWN_SECURITY_TYPE_IDS.has(id)),
  )];
  const unknownNames = [...new Set(
    refused.filter((r) => r.id == null)
      .map((r) => (r.name ?? '').trim())
      .filter((name) => !KNOWN_SECURITY_TYPE_NAMES.has(name.toLowerCase()))
      .map((name) => (name === '' ? '(none)' : name)),
  )];
  if (unknownIds.length === 0 && unknownNames.length === 0) return;

  const parts = [
    unknownIds.length > 0 ? `type id(s) ${unknownIds.join(', ')}` : '',
    unknownNames.length > 0 ? `untyped row(s) named ${unknownNames.join(', ')}` : '',
  ].filter(Boolean);

  // eslint-disable-next-line no-console
  console.warn(
    `[reallocation-v2] ${context}: refused ${parts.join(' and ')}, which the type allow-lists do `
    + 'not recognise. They may be new, or Orion may have sent the row without a type. Check '
    + 'KNOWN_SECURITY_TYPE_IDS and whether they belong on the buy or sell allow-list.',
  );
}

/** Map an Eclipse security-master row to the picker's `SecurityOption`. */
/**
 * How well a security answers the search term: lower is better, null does not answer it at all.
 *
 *   0  the ticker IS the term          -- "AAPL" must put AAPL first, always
 *   1  the ticker starts with the term -- "AAP" -> AAPL, the way a symbol lookup behaves
 *   2  a WORD of the name starts with the term -- "apple" -> Apple Inc
 *
 * ⚠️ WORD-START on the name, not "contains", which is what Orion itself does server-side. A
 * contains match makes "apple" return Pineapple Growth Fund, and once name matches are kept
 * rather than discarded that lands in the advisor's list instead of being filtered out behind
 * the scenes. Tokenising on non-alphanumerics also sidesteps escaping the term into a `\b`
 * regex, which a ticker containing a `.` or `-` would otherwise need.
 *
 * ⚠️ Ranked, not scored. There is deliberately no partial credit inside a band -- see the sort
 * in {@link ReallocationV2DataService.searchSecurities} for why Orion's order is kept there.
 */
function matchRank(
  security: OrionEclipseSecurityListResponseModel,
  wanted: string,
): number | null {
  const ticker = (security.symbol ?? '').trim().toUpperCase();
  if (ticker === wanted) return 0;
  if (ticker.startsWith(wanted)) return 1;

  const words = (security.name ?? '').toUpperCase().split(/[^A-Z0-9]+/);
  return words.some((word) => word !== '' && word.startsWith(wanted)) ? 2 : null;
}

function toSecurityOption(security: OrionEclipseSecurityListResponseModel): SecurityOption {
  return {
    // Non-null by the filter above; Eclipse's securityId, not Connect's external id.
    securityId: security.id as number,
    securityTypeId: security.securityTypeId ?? null,
    ticker: security.symbol,
    description: security.name,
    price: security.price ?? null,
    priceAsOf: security.priceDateTime ?? null,
  };
}

/** Map a generated `InvestmentModel` (numeric id) to the UI's `ModelOption`. */
function toModelOption(model: InvestmentModel): ModelOption {
  return {
    id: String(model.id),
    name: model.name,
    // CRM's mapping to Orion Connect. Null until a model is mapped, which is why
    // every consumer keeps a name-based fallback.
    modelAggId: model.orionConnectModelAggID ?? null,
    // One mapper feeds both `models` and `allModels`, so this covers the dropdown and the
    // resolution list together.
    //
    // ⚠️ `?? false` even though the field is non-nullable on the wire. `ModelOption.isSma` is
    // declared `boolean`, and without this an absent value makes that declaration a lie —
    // `undefined` flows into a field every SMA guard reads. Harmless where the checks are
    // truthiness tests, but anything comparing `=== false` or spreading the option would be
    // quietly wrong, and a spec fixture is enough to produce it.
    isSma: model.isSma ?? false,
  };
}

/**
 * Resolve an Orion sleeve to the CRM model it represents.
 *
 * ONE definition, deliberately, with three consumers: the store's pre-loaded allocation
 * grid, the store's captured before-state, and the store's SMA lock. If any two of them
 * matched sleeves differently, the same sleeve could be recorded under one model and
 * requested under another — a diff reporting a change nobody made — or an SMA sleeve could
 * resolve for the grid and not for the lock, leaving the account open to a mixed request.
 *
 * ⚠️ This used to say "the SMA block below", which was the load-time refusal ACMTECH-956
 * removed. The consumer is now `ReallocationV2Store.currentSmaRow`, which reads the resolved
 * model off the GRID rather than refusing the account at the door.
 *
 * Returns the whole `ModelOption`, not just its id: the SMA check needs `isSma` off the
 * resolved model, and the store's callers take `?.id ?? ''`.
 */
export function buildModelLookup(
  models: ModelOption[] | undefined,
  allModels: ModelOption[] | undefined,
): (sleeve: SleeveAllocation) => ModelOption | undefined {
  // Resolve against ALL models, not the agent-scoped dropdown list: an account can hold a
  // sleeve on a model this advisor is not offered, and that row still has to show what it
  // is. Falls back to the dropdown list if the wider lookup failed.
  const resolvable = allModels?.length ? allModels : models ?? [];
  const byAggId = new Map(
    resolvable.filter((m) => m.modelAggId != null).map((m) => [m.modelAggId as number, m] as const),
  );
  const byName = new Map(resolvable.map((m) => [m.name.trim().toLowerCase(), m] as const));

  // Id first — an exact join, immune to spelling. Name second, for models CRM has not
  // mapped an OrionConnectModelAggID for yet.
  return (s) =>
    (s.modelAggId != null ? byAggId.get(s.modelAggId) : undefined)
    ?? byName.get(s.modelAggName.trim().toLowerCase());
}

/**
 * The model an account carries at ACCOUNT level, as a SLEEVE the rest of the wizard can use.
 *
 * Orion can hold a model on the account itself, with no Connect sleeve behind it. Such an account
 * reports no `accountSleeves`, so the allocation grid used to open blank and say nothing — the
 * advisor was shown an empty picker on an account that already holds a model.
 *
 * ⚠️ Returns a synthetic {@link SleeveAllocation}, which ACMTECH-951 deliberately did NOT do.
 * Its three objections were all sound at the time and are all now void:
 *
 *   1. cash counted twice — once inside `modelSleeveValue`, once as `totalCash`
 *   2. anything SOLD counted twice
 *   3. kept non-cash positions swept into a pool the design excludes them from
 *
 * All three came from the account's holdings ALSO being loaded as step-1 positions. A modelled
 * whole account now yields no positions at all (see `findPositionSource`), so there is nothing
 * left to double-count, and the sleeve shape is what makes the seven things keyed off
 * `data.sleeves` — the CURRENT columns, the change baseline, `modelSetChanged`,
 * `modelSleeveValue`, the minimums, `buildCurrentModelAllocations`, the snapshot — work without
 * seven special cases. Two representations of "the model this account holds" is exactly the
 * drift this file has been bitten by before.
 *
 * The whole account value, because the model owns all of it. Target, distribution and
 * contribution are 100: one model holding everything is what the account IS.
 *
 * ⚠️ Resolution is THREE routes tried in order of directness, and the first is why Eclipse's own
 * id cannot simply be used as the model id:
 *
 *   Eclipse row .modelId  (Eclipse id space)
 *     1. modelAggs.find(a => a.eclipseModelId === modelId).id        (Connect agg id)
 *     2. eclipseModels[modelId].communityModelId
 *          -> modelAggs.find(a => a.communityModelId === that).id    (community models)
 *     3. name, inside buildModelLookup, when neither id route matched
 *   -> buildModelLookup byAggId -> the CRM model                     (via OrionConnectModelAggID)
 *
 * All three run on data already fetched — the raw Eclipse rows, the firm-wide agg list (loaded
 * for the minimums) and Eclipse's model list — so this costs no extra round trip.
 *
 * ⚠️ Neither id route is guaranteed. `eclipseModelId` is nullable, and is null on every
 * community model; `communityModelId` is null on everything that is not one. The name fallback
 * is not decoration — it is the path an ordinary model with an unpopulated cross-reference
 * takes.
 *
 * ⚠️ Eclipse's `modelId` must NEVER be poured into `SleeveAllocation.modelAggId`. They are
 * different id spaces — CRM 181 vs Eclipse 6 for the same model — and the agg list is the only
 * legitimate bridge between them.
 *
 * Returns null when the account is sleeved (Connect is authoritative there and must not be
 * second-guessed) or carries no model at all.
 *
 * @throws AccountModelUnresolvedError when a model IS present but no route identifies it.
 */
function resolveAccountLevelSleeve(
  rows: OrionEclipseAccountDetailModel[],
  sleeveAllocations: SleeveAllocation[],
  // Nullable as well as optional: the agg lookup is non-fatal and returns null on failure, in
  // which case the id hop simply misses and the name fallback carries it.
  modelAggs: OrionConnectTradingModelAggModel[] | null | undefined,
  // Same: null when Eclipse's model list could not be read, which costs the community hop.
  eclipseModels: OrionEclipseModelListResponseModel[] | null | undefined,
  models: ModelOption[] | undefined,
  allModels: ModelOption[] | undefined,
): SleeveAllocation | null {
  // A sleeved account's models come from Connect. Only look at the account itself when Connect
  // gave us nothing to work from.
  if (sleeveAllocations.length > 0) return null;

  // The same predicate `findPositionSource` used to decide there are no positions here, so the
  // two cannot disagree about whether this account is a model.
  const name = accountLevelModelName(rows);
  if (!name) return null;

  const [row] = rows;

  // Hop one: Eclipse model id -> Connect agg id, via the agg list's own cross-reference.
  const agg = row.modelId != null
    ? (modelAggs ?? []).find((a) => a.eclipseModelId != null && a.eclipseModelId === row.modelId)
    : undefined;

  // Hop two: Eclipse model id -> CommunityModelId -> Connect agg id.
  //
  // ⚠️ Orion manages some models centrally -- "community models" -- and links those between
  // Eclipse and Connect by CommunityModelId, NOT by EclipseModelId, which is null on such an
  // agg. So hop one above cannot resolve them, and neither can the name below: Eclipse PREFIXES
  // the name where Connect does not. Both misses are structural rather than data gaps, which is
  // why a third hop is needed rather than better matching.
  //
  // Worked example, captured 2026-09-04 from account 61797116:
  //
  //   Eclipse model 56   "VAN Vanguard CRSP 80% Equity/ 20% Fixed Income - ETF"
  //                      IsCommunityModel true, CommunityModelId 192256
  //   Connect agg 53     "Vanguard CRSP 80% Equity/ 20% Fixed Income - ETF"   <- no "VAN "
  //                      EclipseModelId NULL, CommunityModelId 192256
  //
  // The Eclipse row itself carries only `modelId`, which is why the model LIST is fetched: it
  // is what maps 56 -> 192256. Two lookups, both on data already in hand.
  //
  // ⚠️ Guarded on a non-null CommunityModelId at BOTH ends. Matching null to null would join
  // every non-community model on either side to the first one on the other -- silently, and
  // with a plausible-looking result.
  //
  // ⚠️ Only the ACCOUNT-LEVEL path needs any of this. A sleeved account gets `modelAggId`
  // straight from Connect and never makes the crossing.
  //
  // Corroborating fields, if this route ever needs a second opinion: Eclipse's `SleeveSuffix`
  // equals Connect's `AccountNumberSuffix` ("VAN Vanguard CRSP 80"), and both records are
  // created by api_community_service@orionadvisor.com.
  const eclipseModel = row.modelId != null
    ? (eclipseModels ?? []).find((m) => m.id === row.modelId)
    : undefined;
  const communityId = eclipseModel?.communityModelId ?? null;
  const communityAgg = communityId != null
    ? (modelAggs ?? []).find((a) => a.communityModelId === communityId)
    : undefined;

  // Id joins first, in order of directness; the name is the acknowledged weak one and stays last.
  const bridged = agg ?? communityAgg;

  // Then the SAME lookup a sleeve goes through -- which carries hop three, the name -- so that an
  // account-level model and a sleeved one can never resolve to different CRM models from the
  // same inputs.
  const resolved = buildModelLookup(models, allModels)({
    accountId: null,
    modelAggId: bridged?.id ?? null,
    modelAggName: name,
    target: 0,
    distribution: 0,
    contribution: 0,
    minimum: null,
    currentValue: 0,
  });

  // ⚠️ Diagnostics before the throw, because "cannot identify" on its own is unactionable.
  //
  // Four separate things can break this chain, and the advisor-facing message cannot tell
  // them apart -- but whoever is asked to fix the account needs to know which:
  //
  //   1. Orion has not populated `eclipseModelId` on the agg, so hop one finds nothing.
  //   2. Either reference lookup failed; both are non-fatal and return null, so the hop that
  //      needed it misses silently and the name is left carrying the whole resolution.
  //   3. The model is a community model whose `communityModelId` does not match on the Connect
  //      side either, which would be new -- the pair is set by the same Orion service.
  //   4. CRM has no `OrionConnectModelAggID` on the model row, so it is absent from `byAggId`
  //      and only the name can match it.
  //
  // In all four the name has to match exactly after trim and lower-case, which is what fails on
  // punctuation drift, Orion's `.e` suffix on Eclipse-managed models, or the "VAN "-style prefix
  // Eclipse puts on a community model.
  //
  // See ACMTECH-950: the name fallback exists precisely because that column is not backfilled,
  // so an account failing here is usually a symptom of that gap rather than a fault in this.
  const resolvable = allModels?.length ? allModels : models ?? [];
  const mapped = resolvable.filter((m) => m.modelAggId != null).length;
  // ⚠️ The agg COVERAGE count is the one that tells you whether hop one is viable at all.
  //
  // "no agg matched" on its own is ambiguous: it reads as "this model is not cross-referenced",
  // but it looks identical when `eclipseModelId` is null across the whole agg list -- in which
  // case the id hop cannot resolve ANY account and every one of them is riding on the name.
  // A ratio separates a one-model gap from a dead path, which are very different problems.
  const xrefd = (modelAggs ?? []).filter((a) => a.eclipseModelId != null).length;
  // Same reasoning for the community route, and the two counts are complementary: an agg
  // carries one cross-reference or the other, so a low `xrefd` is only alarming if `community`
  // does not account for the rest.
  const community = (modelAggs ?? []).filter((a) => a.communityModelId != null).length;
  const diagnosis = `Eclipse modelId=${row.modelId ?? 'null'}; `
    + `Connect aggs ${modelAggs == null ? 'UNAVAILABLE (that lookup failed)' : `loaded (${modelAggs.length}, of which ${xrefd} carry an eclipseModelId and ${community} a communityModelId)`}; `
    + `agg matched on eclipseModelId=${agg ? agg.id : 'NONE'}; `
    + `community bridge=${communityAgg ? `agg ${communityAgg.id} via CommunityModelId ${communityId}` : 'NONE'}; `
    + `Eclipse models ${eclipseModels == null ? 'UNAVAILABLE (that lookup failed)' : `loaded (${eclipseModels.length})`}; `
    + `CRM models searched=${resolvable.length}, of which ${mapped} carry an OrionConnectModelAggID`;

  if (!resolved) {
    // eslint-disable-next-line no-console
    console.warn(
      `[reallocation-v2] Account-level model "${name}" resolved to no CRM model, so the request `
      + 'is being refused. Every route missed -- neither id join found an agg AND the name did '
      + `not match. ${diagnosis}. See ACMTECH-950.`,
    );
    throw new AccountModelUnresolvedError(name);
  }

  // 🟢 Resolved, but only by NAME -- which works until somebody renames the model on either
  // side. Worth saying out loud rather than leaving as a quiet success: this is the same
  // missing cross-reference that turns into the hard failure above, caught one step earlier.
  if (!bridged) {
    // eslint-disable-next-line no-console
    console.warn(
      `[reallocation-v2] Account-level model "${name}" resolved by NAME, not by id -- neither `
      + 'the Eclipse cross-reference nor the community bridge had anything to offer. It will '
      + `stop resolving if either side is renamed. ${diagnosis}. See ACMTECH-950.`,
    );
  }

  return {
    // ⚠️ Eclipse's `accountId`, NOT its `id` — see `connectAccountIdOf`. This is a real Connect
    // account id, so the row reads as one the account already holds rather than one the advisor
    // added: the model cell stays read-only, `modelSetChanged` stays false at load, and the
    // `{ accountId, modelAggId }` pair ACMTECH-903 patches is whole.
    accountId: connectAccountIdOf(row),
    // The agg id when EITHER id hop found one -- the direct Eclipse cross-reference or the
    // community bridge -- and null when only the name resolved. Null is honest here: half the
    // pair is worth less than the pair, and pretending otherwise would send the trade team an
    // id that resolves to nothing.
    modelAggId: bridged?.id ?? null,
    // The CRM model's own name, not Eclipse's spelling, so `buildModelLookup` resolves this
    // sleeve back to the same model it was built from.
    modelAggName: resolved.name,
    target: 100,
    distribution: 100,
    contribution: 100,
    // Connect reports no minimum for an account that is not sleeved. Null means "not on
    // record", which `buildModelMinimums` already distinguishes from zero.
    minimum: null,
    // The WHOLE account. The model owns the cash as well as the securities, which is why
    // nothing is left to allocate on a request that only restates the same model.
    currentValue: rows.reduce((sum, r) => sum + (r.accountValue?.totalValue ?? 0), 0),
  };
}

/**
 * The model an UNSLEEVED account carries at account level, by name, or null when it carries none.
 *
 * ONE definition with two consumers that must agree: {@link findPositionSource}, which decides
 * whether the account has any positions of its own to sell, and
 * {@link resolveAccountLevelSleeve}, which turns it into a model row. If those two disagreed the
 * wizard would list a model's holdings as sellable AND show the model, double-counting the same
 * money -- or show neither and lose the account.
 *
 * Three things are not an account-level model, and each for its own reason:
 *
 *   - more than one Eclipse row: the account is sleeved, so its models come from Connect
 *   - no model name at all: nothing is assigned, so every holding is hand-traded
 *   - Custom Holdings: the hand-traded bucket is not a model allocation
 */
function accountLevelModelName(rows: OrionEclipseAccountDetailModel[]): string | null {
  if (rows.length !== 1) return null;
  const name = (rows[0].model ?? '').trim();
  if (!name || name.toLowerCase() === customHoldingsModelName.toLowerCase()) return null;
  return name;
}

/**
 * Replace each Connect sleeve's `currentValue` with what **Eclipse** reports for the same sleeve.
 *
 * ⚠️ **ACMTECH-973: the portal was reading two clocks.** Connect's `currentValue` syncs on its
 * own cadence -- roughly every 15 minutes -- while Eclipse's account totals and holdings are
 * live on the pending basis. Read inside one window they agree; read after the market has moved
 * they do not, and the strip's figures then miss the account total by however far the model
 * sleeves have travelled. On 2026-09-10 that was $829.93 on a $552,642.99 account; the next
 * morning the same account tied to the cent.
 *
 * So this is not a preference between two equally good sources. Every other figure on the screen
 * is Eclipse -- the account total ({@link buildAccountInfo}) and every Custom Holdings position
 * ({@link toPosition}) -- and *the trade team works out of Eclipse*. Connect was supplying the
 * only numbers that are money, from the one system nobody acts in.
 *
 * 🟢 Connect keeps everything it is actually authoritative for, none of which is dollars:
 * `targetAllocation`, `distributionAllocation`, `contributionAllocation`, `modelAggId`,
 * `minimumInvestmentAmount`, and the `{ accountId, modelAggId }` pair the request carries.
 *
 * 🟢 The account-level path already did this -- {@link resolveAccountLevelSleeve} builds its
 * synthetic sleeve by summing Eclipse rows. This makes the ordinary path agree with it, so
 * `SleeveAllocation.currentValue` finally means one thing however the account is set up.
 *
 * ⚠️ **The join is Eclipse's `accountId`, which is CONNECT's account id** -- confirmed by AndyP
 * on 2026-09-11 and by the live observation recorded on {@link connectAccountIdOf}. It is NOT
 * Eclipse's own `id`, and the two live on the same row. Note the same field name means the
 * Eclipse sleeve id on HOLDINGS rows, which is a different payload; see the holdings filter in
 * `loadCustomHoldingsPositions`.
 *
 * ⚠️ **An unmatched sleeve keeps Connect's figure and WARNS.** Zeroing it would read as "this
 * model holds nothing" -- a lie of exactly the kind the old `valueCap` used to tell -- and
 * dropping the sleeve would lose a model the account really holds. Keeping the stale figure is
 * the least-wrong option, and the warning is there because a silent fallback would look like
 * this change having no effect at all. `warnIfPartsDoNotTie` reports the consequence separately.
 *
 * Summed rather than assigned, in case Eclipse ever reports two rows against one Connect
 * account: the sleeve is worth both of them, and `buildAccountInfo` sums the same way.
 */
function withEclipseSleeveValues(
  allocations: SleeveAllocation[],
  rows: OrionEclipseAccountDetailModel[],
  account: AccountModel,
): SleeveAllocation[] {
  const eclipseValueByConnectId = new Map<number, number>();
  for (const row of rows) {
    const connectId = connectAccountIdOf(row);
    if (connectId == null) continue;
    eclipseValueByConnectId.set(
      connectId,
      (eclipseValueByConnectId.get(connectId) ?? 0) + (row.accountValue?.totalValue ?? 0),
    );
  }

  return allocations.map((sleeve) => {
    const eclipseValue = sleeve.accountId == null
      ? undefined
      : eclipseValueByConnectId.get(sleeve.accountId);

    if (eclipseValue == null) {
      // eslint-disable-next-line no-console
      console.warn(
        `[reallocation-v2] Account ${account.accountNumber ?? account.investmentAccountID}: no `
        + `Eclipse row matched Connect sleeve "${sleeve.modelAggName}" `
        + `(accountId ${sleeve.accountId ?? 'missing'}), so it keeps Connect's own value. That `
        + 'figure is on a different clock, so this account may not tie to its reported total.',
      );
      return sleeve;
    }

    return { ...sleeve, currentValue: eclipseValue };
  });
}

/**
 * How far the account's parts may miss its reported total before it is worth saying so.
 *
 * A cent or two is expected and documented: the total is summed from per-sleeve figures while
 * the holdings are summed one by one, and each is independently rounded. Half a dollar is
 * comfortably above that and far below anything that matters, so this catches a real
 * disagreement without crying wolf on arithmetic.
 */
const RECONCILE_TOLERANCE = 0.5;

/**
 * Warn when the account's parts do not add up to the total Orion reports for it.
 *
 * ⚠️ **ACMTECH-973 was found by eye, on a screenshot, on one account.** The figures come from
 * two Orion products on two different clocks -- Eclipse's holdings and account totals are live
 * on the pending basis, while Connect's per-sleeve `currentValue` syncs on its own cadence
 * (roughly every 15 minutes) -- so they agree inside a sync window and drift apart during the
 * trading day. On 2026-09-10 that was $829.93 on a $552,642.99 account; the next morning the
 * same account tied to the cent.
 *
 * This is what makes the next one report itself instead of waiting to be noticed. It is also
 * how the fix gets verified: once the sleeve values come from Eclipse too, this should fall
 * silent and stay silent all day.
 *
 * ⚠️ **Diagnostic only.** It must never block the load or the request: a difference between two
 * reporting systems is not a reason to deny an advisor the screen, and the amounts involved are
 * fractions of a percent. If it starts firing on every account, that is information, not a
 * reason to make it throw.
 *
 * ⚠️ Logs FIGURES and the account number, nothing else. No tickers, no client identity -- this
 * lands in a console that gets screenshotted into tickets.
 *
 * 🟢 At load nothing has been sold and no cash released, so the parts are simply what the
 * account holds: the Custom Holdings positions, its cash, and what the model sleeves report.
 */
function warnIfPartsDoNotTie(
  account: AccountInfo,
  positions: Position[],
  cashPositions: Position[],
  sleeves: SleeveAllocation[],
): void {
  const total = account.totalValue;
  // Nothing to compare against. An account reporting no value at all is its own problem and
  // one this function would only add noise to.
  if (total <= 0) return;

  const customHoldings = [...positions, ...cashPositions]
    .reduce((sum, p) => sum + p.marketValue, 0);
  const models = sleeves.reduce((sum, sl) => sum + sl.currentValue, 0);
  const difference = customHoldings + models - total;
  if (Math.abs(difference) <= RECONCILE_TOLERANCE) return;

  const money = (value: number): string =>
    value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  // eslint-disable-next-line no-console
  console.warn(
    `[reallocation-v2] Account ${account.accountNumber}: the `
    + `parts do not tie to the reported total, off by ${money(difference)}.\n`
    + `  reported total   ${money(total)}\n`
    + `  custom holdings  ${money(customHoldings)}  (Eclipse holdings, pending basis)\n`
    + `  model sleeves    ${money(models)}  (Orion Connect currentValue)\n`
    + '  Connect and Eclipse are different clocks, so a difference here is expected to grow '
    + 'through the trading day and close again overnight -- see ACMTECH-973 before chasing it '
    + 'as a mapping bug.',
  );
}

/**
 * Eclipse's `AccountId` is the ORION CONNECT account id, and it is not `id`.
 *
 * ⚠️ Both live on the same row and they are different id spaces: on account 58082836 Eclipse
 * reports `Id: 1257` (its own) and `AccountId: "1579"` (Connect's). Confirmed against the FIG
 * Data response rather than inferred -- putting the wrong one into the
 * `{ accountId, modelAggId }` pair would hand ACMTECH-903 an id that resolves to nothing.
 *
 * Returned as a number because that is what Connect's own sleeve list gives, and null rather
 * than NaN when absent: half of that pair is worth much less than the pair, so "unknown" has
 * to be expressible.
 */
function connectAccountIdOf(row: OrionEclipseAccountDetailModel): number | null {
  const raw = (row.accountId ?? '').trim();
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

/**
 * Where step 1's positions come from, and whether that is a sleeve or the account itself.
 *
 * The distinction is carried rather than re-derived because the notices depend on it: an
 * unsleeved account has no Custom Holdings sleeve to describe as empty.
 */
interface PositionSource {
  /** The Eclipse row to read holdings from, or undefined when there is nothing to read. */
  row: OrionEclipseAccountDetailModel | undefined;
  /**
   * WHY there is or is not a row, which the notices and the model grid both need.
   *
   * ⚠️ This replaced a boolean called `isWholeAccount`, and the rename is the point. That flag
   * meant "unsleeved", and unsleeved was treated as equivalent to hand-traded -- true when the
   * only unsleeved accounts reaching the wizard held no model, which was the case ACMTECH-915
   * was written for. ACMTECH-951 made MODELLED unsleeved accounts loadable and the equivalence
   * broke silently: a model's own holdings were offered up as individually sellable.
   *
   *   customHoldings — the account's Custom Holdings sleeve. Its holdings are hand-traded.
   *   wholeAccount   — one row, no model: a SYNTHETIC Custom Holdings sleeve. The trade team
   *                    usually assigns Custom Holdings eventually, but the wizard can open
   *                    before that and the account behaves the same either way.
   *   modelled       — one row carrying a real model. The model owns the account, cash
   *                    included, so there is nothing here to sell. No row, and no positions
   *                    call either.
   *   none           — sleeved, but with no Custom Holdings sleeve to read.
   */
  kind: 'customHoldings' | 'wholeAccount' | 'modelled' | 'none';
}

/**
 * Pick the hand-traded source out of an account's Eclipse rows.
 *
 * Id-primary, name-fallback (see `customHoldingsEclipseModelID`). The two are checked
 * independently and a disagreement is logged rather than silently resolved: they can
 * only diverge if an environment assumption broke, and quietly preferring one would
 * hide that.
 *
 * **The unsleeved account is the fallback case, identified by row count alone.** One
 * Eclipse row means the account is not sleeved and that row *is* the account — the same
 * premise Initial Account relies on when it takes `details[0]`. It is NOT identified by
 * `isSleeve === 0`: Eclipse reports `isSleeve: 1` on every row including the bare
 * custodial parent, so that field discriminates nothing.
 *
 * ⚠️ This used to additionally require the row's `accountNumber` to equal the CRM account
 * number, with hyphens stripped from the CRM side only. That comparison could not
 * succeed for a hyphenated account: CRM `4477-1166` normalised to `44771166` while
 * Eclipse reported `4477-1166`, so every such account fell through to "no Custom Holdings
 * sleeve" and showed an empty step 1. The spec fixture had no hyphen, so the case was
 * invisible. Two id-less strings from different systems were never a safe key; the row
 * count already answers the question the match was asking.
 *
 * The `sleeves.length !== 1` gate stays and is load-bearing: a sleeved account with no
 * Custom Holdings sleeve still has a custodial parent row, and returning it would hand
 * back the $0 parent as though it were the hand-traded bucket — reporting an *empty*
 * sleeve where the truth is a *missing* one.
 */
function findPositionSource(sleeves: OrionEclipseAccountDetailModel[]): PositionSource {
  const byId = sleeves.find((s) => s.modelId === customHoldingsEclipseModelID);
  const byName = sleeves.find((s) => s.model === customHoldingsModelName);

  if (byId && byName && byId.id !== byName.id) {
    // eslint-disable-next-line no-console
    console.warn(
      `[reallocation-v2] Custom Holdings sleeve is ambiguous: Eclipse modelId ${customHoldingsEclipseModelID} `
      + `selects account ${byId.id} but the model name selects ${byName.id}. Using the id.`,
    );
  }

  const customHoldings = byId ?? byName;
  if (customHoldings) return { row: customHoldings, kind: 'customHoldings' };

  if (sleeves.length !== 1) return { row: undefined, kind: 'none' };

  // ⚠️ A single row carrying a real model has NO positions of its own -- not even cash.
  //
  // Every holding belongs to the model, so none of them is the advisor's to sell line by line,
  // and the cash rides inside the model's allocation rather than being custodial cash they can
  // set aside. Returning no row means `loadCustomHoldingsPositions` short-circuits, so the
  // holdings call is skipped rather than fetched and filtered.
  //
  // Without a model the same row IS the hand-traded bucket -- see `kind`.
  if (accountLevelModelName(sleeves)) return { row: undefined, kind: 'modelled' };
  return { row: sleeves[0], kind: 'wholeAccount' };
}

/**
 * Explain an empty step 1, or null when there is something to show.
 *
 * Three outcomes, because "there is nothing here" has three different meanings and only
 * one of them is worth a second look from the advisor.
 *
 * ⚠️ CASH counts as something to show. The notice replaces the whole step body, so returning one
 * while the sleeve holds cash would hide the release controls behind an explanation that the step
 * is empty -- and that cash could then never enter a request. Since cash became opt-in, a sleeve
 * with cash and no securities is a step with a real decision on it.
 */
function noticeFor(
  source: PositionSource,
  positions: Position[],
  cashPositions: Position[],
): string | null {
  if (positions.length > 0 || cashPositions.length > 0) return null;
  switch (source.kind) {
    case 'none': return NO_CUSTOM_HOLDINGS_SLEEVE_NOTICE;
    // Naming a Custom Holdings sleeve here would invent one: neither of these accounts has
    // sleeves, and whatever holdings they have are already listed above -- there just are not
    // any to list.
    //
    // `modelled` deliberately shares this line rather than getting its own. A bespoke "invested
    // entirely through its model" wording was tried and dropped as more words than the case
    // needs: the advisor is looking at an empty list, and what they need is the instruction to
    // continue, which this already carries.
    case 'wholeAccount':
    case 'modelled':
      return EMPTY_ACCOUNT_NOTICE;
    // 🔴 A CUSTOM HOLDINGS SLEEVE WITH NOTHING IN IT MEANS TWO DIFFERENT THINGS, and the sleeve's
    // own reported value is what tells them apart. Worth nothing and holding nothing agree with
    // each other; worth something and holding nothing do not.
    default:
      return customHoldingsLooksUnavailable(source)
        ? CUSTOM_HOLDINGS_UNAVAILABLE_NOTICE
        : EMPTY_CUSTOM_HOLDINGS_SLEEVE_NOTICE;
  }
}

/**
 * Whether the Custom Holdings sleeve reported a balance while returning no holdings.
 *
 * ⚠️ CALLED ONLY WHERE THE HOLDINGS ARE ALREADY KNOWN TO BE EMPTY -- `noticeFor`'s first line
 * returns early otherwise, and the step's flag is set from the same pair of conditions. It does
 * not re-test that here, so do not call it from anywhere that has not.
 */
function customHoldingsLooksUnavailable(source: PositionSource): boolean {
  return source.kind === 'customHoldings' && (source.row?.accountValue?.totalValue ?? 0) > 0;
}

/**
 * Reduce Connect's sleeve list to the model sleeves step 4 pre-loads.
 *
 * ⚠️ **`sleeveType === 'Normal sleeve'` is necessary but NOT sufficient.** It correctly
 * drops the `Custodial account` parent and the system `Contribution` / `Distribution`
 * sleeves, but **Custom Holdings also reports as a Normal sleeve** — with 0/0/0
 * allocations. Filtering on `sleeveType` alone pre-loads a phantom 0% row that can
 * never be satisfied, so Custom Holdings is excluded by name as well.
 *
 * ⚠️ `targetBalanceType` must read `Percent`. On a `Dollars` registration these fields
 * are amounts, not percentages, and feeding them into a grid that totals to 100 would
 * be silently wrong. Such sleeves are dropped rather than misread.
 */
function toSleeveAllocations(
  sleeves: OrionConnectAccountSleeveModel[] | undefined,
): SleeveAllocation[] {
  return (sleeves ?? [])
    .filter((s) => s.sleeveType === NORMAL_SLEEVE_TYPE)
    .filter((s) => (s.modelAgg ?? '').trim().toLowerCase() !== customHoldingsModelName.toLowerCase())
    .filter((s) => {
      if (s.targetBalanceType === PERCENT_BALANCE_TYPE) return true;
      // eslint-disable-next-line no-console
      console.warn(
        `[reallocation-v2] Skipping sleeve "${s.modelAgg}": targetBalanceType is `
        + `"${s.targetBalanceType}", so its allocations are not percentages.`,
      );
      return false;
    })
    .map((s) => ({
      accountId: s.accountId ?? null,
      modelAggId: s.modelAggId ?? null,
      modelAggName: s.modelAgg ?? '',
      // Independent of one another - never derive one from another, and never parse
      // sleeveDescription ("... 41.0000 Percent"), which is display text.
      target: s.targetAllocation ?? 0,
      distribution: s.distributionAllocation ?? 0,
      contribution: s.contributionAllocation ?? 0,
      minimum: s.minimumInvestmentAmount ?? null,
      currentValue: s.currentValue ?? 0,
    }));
}

/**
 * Minimum investment per model, indexed by Connect model-aggregate id **and** by name.
 *
 * The id index is the real answer now that CRM maps `OrionConnectModelAggID`. The name
 * index is the fallback for models that mapping does not cover yet — it is what the
 * whole lookup relied on before, and dropping it would regress every unmapped model
 * from "resolves by name" to "unresolved", which blocks Next.
 *
 * Built from the firm-wide model-agg list, then overlaid with the account's own
 * sleeves, which are the more specific source. Returns null when the model-agg lookup
 * was unavailable — see {@link ModelMinimums}.
 *
 * A disagreement between the two Connect surfaces is logged rather than silently
 * resolved: it means one of them is stale and an advisor is being shown a wrong
 * threshold.
 */
function buildModelMinimums(
  aggs: OrionConnectTradingModelAggModel[] | null,
  sleeves: SleeveAllocation[],
  allModels: ModelOption[],
): ModelMinimums | null {
  if (!aggs) return null;

  const byModelAggId: Record<number, number> = {};
  const byName: Record<string, number> = {};

  for (const agg of aggs) {
    if (agg.minimumInvestmentAmount == null) continue;
    if (agg.id != null) byModelAggId[agg.id] = agg.minimumInvestmentAmount;
    const name = (agg.name ?? '').trim().toLowerCase();
    if (name) byName[name] = agg.minimumInvestmentAmount;
  }

  // The account's own sleeves win over the firm-wide list for the same model.
  for (const sleeve of sleeves) {
    if (sleeve.minimum == null) continue;
    const name = sleeve.modelAggName.trim().toLowerCase();

    const previous = sleeve.modelAggId != null ? byModelAggId[sleeve.modelAggId] : byName[name];
    if (previous != null && Math.abs(previous - sleeve.minimum) > 0.005) {
      // eslint-disable-next-line no-console
      console.warn(
        `[reallocation-v2] Minimum for "${sleeve.modelAggName}" disagrees between Connect surfaces: `
        + `model aggregates say ${previous}, the account's sleeve says ${sleeve.minimum}. `
        + 'Using the sleeve. One of the two is stale.',
      );
    }

    if (sleeve.modelAggId != null) byModelAggId[sleeve.modelAggId] = sleeve.minimum;
    if (name) byName[name] = sleeve.minimum;
  }

  warnOnUnmappedModelsWithMinimums(allModels, byName);

  return { byModelAggId, byName };
}

/**
 * Flag models that still resolve only by name, so the CRM mapping's coverage gaps are
 * visible rather than inferred from a blank dropdown.
 *
 * Only models that actually have a minimum on record are worth reporting — an unmapped
 * model with no minimum anywhere costs nothing.
 */
function warnOnUnmappedModelsWithMinimums(
  allModels: ModelOption[],
  byName: Record<string, number>,
): void {
  const unmapped = allModels.filter(
    (m) => m.modelAggId == null && byName[m.name.trim().toLowerCase()] != null,
  );
  if (unmapped.length === 0) return;

  // eslint-disable-next-line no-console
  console.warn(
    `[reallocation-v2] ${unmapped.length} model(s) have a Connect minimum but no `
    + 'OrionConnectModelAggID in CRM, so they resolve by name only: '
    + unmapped.map((m) => m.name).join(', '),
  );
}

/** Reduce Eclipse's portfolio detail to the handful of fields this workflow cares about. */
function toPortfolioContext(
  portfolioId: number,
  portfolio: OrionEclipsePortfolioDetailModel | null,
): PortfolioContext | null {
  if (!portfolio) return null;
  return {
    portfolioId,
    registrationId: portfolio.general?.registrationId ?? null,
    // General.doNotTrade is the flag; issues.doNotTrade is a count. Not interchangeable.
    doNotTrade: portfolio.general?.doNotTrade ?? false,
    fixedIncomeTradeBlockDate: portfolio.general?.fixedIncomeTradeBlockDate ?? null,
    isDisabled: portfolio.isDisabled === 1,
    disabledReason: portfolio.disabledReason || null,
    setAsideCash: portfolio.summary?.aum?.totalCash?.setAsideCash ?? null,
    reportedTotalValue: portfolio.summary?.aum?.totalValue ?? null,
  };
}

/** Whether "Yes"/"No" style Orion flags are truthy. */
/*
 * ⚠️ THERE IS NO ASSET-CATEGORY SELL RULE. Every position reaching step 1 is sellable.
 *
 * Removed on 2026-09-04 (ACMTECH-902) after the rule locked accounts it was never meant to.
 * Cash is still excluded, but by `isCash` at the split below - not by category, and not here.
 *
 * ⚠️ READ THIS BEFORE WRITING ANOTHER ONE. The removed rule was an allow-list over
 * `assetCategoryName` - Bond, Equity, Money Market, Alternative Investments - compared by
 * normalised equality, failing closed. It was reasonable and it did not work, for a reason
 * that will defeat the next attempt built on the same field:
 *
 *   ORION RETURNS EITHER THE CATEGORY NAME OR ITS ABBREVIATION IN THAT ONE FIELD.
 *
 * Connect's Asset Categories reference table pairs CategoryAbbr with CategoryName - B/Bond,
 * E/Equity, M/Money Market, Alt Inv/Alternative Investments - AND carries separate records
 * whose name IS the abbreviation (B/B, E/E, FIXED INCOME, UNASSIGNED, OTHER). So a single
 * `assetCategoryName` can legitimately be "Money Market" on one holding and "M" on the next.
 *
 * The case that broke it: SNVXX, Schwab Government Money Fund, `assetCategoryName: "M"`,
 * `assetClassName: "Cash"`, `assetSubClassName: "Money Market-Taxable"`, `isCash: No`,
 * `securityTypeName: "Mutual Fund"`. A money market fund - the exact holding "Money Market"
 * was on the list to permit - locked because it arrived abbreviated. It was 99% of that
 * account, so nothing in the request could be sold at all.
 *
 * ⚠️ And `securityTypeId` cannot replace it. Eclipse's security types are Mutual Fund 1,
 * CASH 2, CD 4, Equity 5, Bond 6, Option 7, Annuity 9, Other 10, Miscellaneous 11 - that is
 * the complete active set, and NOTHING in it is structured. Structured Note, Structured CD
 * and Structured Product exist only as asset CATEGORIES (SN, SCD, SP), so a type-based gate
 * cannot see the one thing the rule existed to block; a Structured CD would arrive as CD.
 *
 * 🟢 What a future rule needs, so this is not re-derived a third time:
 *
 *   1. Accept BOTH forms of every category it allows, or normalise abbr -> name first. The
 *      abbreviations of the four originally-allowed categories collide with nothing blocked
 *      (SN, SCD, SP, OTHER, UNASSIGNED), so accepting them is safe.
 *   2. Decide FIXED INCOME, OTHER and UNASSIGNED deliberately. The old list blocked all
 *      three by omission rather than by decision.
 *   3. Warn on any category the rule does not recognise. This failure was silent - a greyed
 *      checkbox and nothing in the console - which is why it reached a pilot advisor.
 *
 * The lock MECHANISM is deliberately still here and intact: `Position.sellable`, the disabled
 * control in `buildPositionsForm`, the N/A cell, the `toggleSellAll` skip and `orderForStepOne`.
 * Only the predicate is gone, so restoring a rule means writing one function - see git
 * 362cce4c for the removed version.
 */

function isYes(flag: string | undefined): boolean {
  return (flag ?? '').trim().toUpperCase() === 'YES';
}

/**
 * Real tickers A-Z first, then anything starting with a digit -- CUSIPs, which is what those
 * rows are. Case-insensitive within each group.
 *
 * ⚠️ The digit test is the PRIMARY key, and it has to be, because a plain string sort puts
 * digits above letters and buries the tickers under the CUSIPs. An advisor scanning for a
 * holding is looking for a ticker; the CUSIP rows are the ones only identifiable from their
 * description, so they belong at the bottom rather than in the way.
 *
 * A blank symbol counts as a letter and sorts to the very top. Not a case worth designing
 * for -- Orion has always given a symbol -- but it stays visible rather than hiding at the
 * end of a long list, which is the right way round for something that needs looking at.
 */
const byTicker = (a: Position, b: Position): number => {
  const aIsCusip = /^\d/.test(a.ticker);
  const bIsCusip = /^\d/.test(b.ticker);
  if (aIsCusip !== bIsCusip) return aIsCusip ? 1 : -1;
  return a.ticker.localeCompare(b.ticker, 'en', { sensitivity: 'base' });
};

/**
 * Step 1 display order: tickers A-Z, then CUSIP rows, with any locked rows below both.
 *
 * Orion returns holdings in no order the advisor can use, so a long list meant hunting for a
 * ticker. Alphabetical is the order someone scanning for one expects -- see {@link byTicker}
 * for why the numeric symbols sort last rather than first.
 *
 * ⚠️ Ordered HERE, not in the template, because it has to be the order the form is built
 * in. `buildPositionsForm` walks this array to create the controls and `positions()` pairs
 * row `i` with `positionsForm.at(i)`, so the view and the form must agree. Sorting in the
 * `@for` would leave every row wired to another row's controls - a share count typed against
 * one holding landing on a different one.
 *
 * The sort is stable, so two rows carrying the same ticker keep Orion's order relative to
 * each other. Nothing depends on that; it just means the list cannot reshuffle between loads.
 *
 * ⚠️ The partition is currently a no-op, and kept anyway. Nothing sets `sellable: false`
 * since the asset-category rule was removed, so every row lands in the first group. Kept
 * because it is half the lock mechanism: delete it and restoring a rule means rediscovering
 * why the ordering cannot live in the template.
 */
export function orderForStepOne(positions: Position[]): Position[] {
  return [
    ...positions.filter((p) => p.sellable).sort(byTicker),
    ...positions.filter((p) => !p.sellable).sort(byTicker),
  ];
}

/**
 * Who the account is for: every client on it, joined, falling back to the registration title.
 *
 * Exported because the wizard shows this twice — the loading panel reads it from the
 * `account` input before any of this service has run, and the header reads it back off
 * `data().account` afterwards. Deriving it twice let the two disagree: the loading panel used
 * `title` alone, which is only the FALLBACK, so on any account with clients attached it
 * showed nothing at all where a name was expected.
 */
export function deriveClientName(account: AccountModel): string {
  const joined = account.clients
    ?.map((c) => `${c.firstName} ${c.lastName}`.trim())
    .filter(Boolean)
    .join(' & ');
  return joined || account.title || '';
}

/** Filter the displayable rows and map them, whichever endpoint supplied them. */
function toPositions(
  holdings: OrionEclipseAccountHoldingsResponseModel[] | undefined,
  accountTotal: number,
): Position[] {
  const shown = (holdings ?? []).filter(isDisplayableHolding);

  // ⚠️ REPOINTED, and it had to be. This used to report rows the sell rule REFUSED -- and nothing
  // is refused any more, so left alone it would have gone permanently quiet at the exact moment
  // the type started deciding something new.
  //
  // It now reports rows arriving with NO USABLE TYPE, which is the open question: those are held
  // as cash on the assumption that we cannot prove them safe to spend. If AndyP is right that
  // every Custom Holdings row is typed, this never fires and costs nothing. If it does fire, we
  // have learned something rather than having quietly withheld an ordinary advisor's money.
  warnOnRefusedSecurityTypes(
    shown.filter((h) => h.securityTypeId == null)
      .map((h) => ({ id: h.securityTypeId, name: h.securityTypeName })),
    'Custom Holdings positions (untyped, so held for review on sale)',
  );
  return shown.map((h) => toPosition(h, accountTotal));
}

/** Skip deleted rows and rows with neither shares nor value. */
function isDisplayableHolding(h: OrionEclipseAccountHoldingsResponseModel): boolean {
  if (h.isDeleted === 1) return false;
  return (h.shares ?? 0) !== 0 || (h.value ?? 0) !== 0;
}

/**
 * Map an Orion Eclipse holding to a workflow `Position`.
 *
 * `marketValue` takes `pendingValue`, not `value`. Every total Orion itself reports —
 * a sleeve's `accountValue.totalValue`, the portfolio's `summary.aum.totalValue`, and
 * Connect's per-sleeve `currentValue` — is pending-based.
 *
 * ⚠️ They reconcile to the cent **only within a Connect sync window** (ACMTECH-973). Connect
 * refreshes roughly every 15 minutes while Eclipse is live, so during the trading day its
 * figure lags by however far the sleeve has moved — $829.93 on one observed account. That is
 * why `withEclipseSleeveValues` re-values the sleeves from Eclipse rather than trusting the
 * two sources to agree. Summing `value` gives a market-basis figure no Orion surface reports, and the
 * grid rows would not add up to the account total shown above them. The two bases
 * differ by well under a percent, and on many accounts not at all, so a regression
 * here is easy to miss: verify on an account where they diverge.
 */
function toPosition(h: OrionEclipseAccountHoldingsResponseModel, accountTotal: number): Position {
  const cash = isYes(h.isCash);
  const marketValue = h.pendingValue ?? h.value ?? 0;
  return {
    // ⚠️ Passed through, NOT defaulted. Null here means Orion did not type the row, and CRM
    // needs that to stay distinguishable from a hand-typed security -- see ACMTECH-976.
    securityTypeId: h.securityTypeId ?? null,
    securityId: h.securityId ?? null,
    ticker: h.securitySymbol,
    description: h.securityName,
    shares: h.shares ?? null,
    // Derived from the value being shown rather than taken from h.price, so the row
    // multiplies out. Orion's `price` is a market price while `marketValue` above is the
    // PENDING value, and on rows where the two bases diverge the grid would otherwise
    // print a shares x price that does not equal the market value beside it — off by
    // $168 on a $8,859 position in one observed case. Advisors do that arithmetic.
    //
    // The trade-off is deliberate: this price can differ in the cents from the one Orion
    // Eclipse displays. Internal consistency wins because the alternative is showing
    // `value`, which would break the reconciliation the pending basis was chosen for —
    // the sleeve and account totals would then match no Orion surface at all.
    price: derivePrice(marketValue, h.shares) ?? h.price ?? null,
    marketValue,
    // Reported as-is, including zero. Only a null reads as "not on file" — see the note
    // on reportedGainLoss below for why a zero is worth a second look on this endpoint.
    totalCost: h.costAmount ?? null,
    isCash: cash,
    assetCategoryName: h.assetCategoryName ?? null,
    // ⚠️ UNCONDITIONAL AGAIN (2026-09-14). Every security type may be sold; what differs is
    // where the proceeds may go, which `requiresReview` below carries.
    //
    // The lock MECHANISM is left wired -- `Position.sellable`, the disabled control in
    // `buildPositionsForm`, the N/A cell, the `toggleSellAll` skip -- so a future sell rule is
    // one predicate rather than a rebuild. That is the third time this has gone round; see the
    // block comment above `orderForStepOne`.
    sellable: true,
    // ⚠️ NOT the inverse of `sellable`, and they must never be conflated. A row requiring review
    // IS sellable: its checkbox and its share input are both enabled. What changes is that its
    // proceeds are held as cash instead of joining the investable pool.
    requiresReview: requiresSellReview(h),
    // Unchanged: "do not trade this" does not conflict with "cannot sell this", and the
    // DNT step is off anyway (REALLOCATION_V2_DO_NOT_TRADE_STEP_ENABLED).
    dntEligible: !cash,
    // Deliberately NOT h.currentInPer, even though the portfolio-scoped endpoint reports
    // that account-relative and it would satisfy the "% of Acct" column directly.
    //
    // Two reasons to derive instead. Its denominator is Orion's portfolio total, while
    // the figure shown above the grid is our own sum of each sleeve's
    // accountValue.totalValue - the two agree to the cent, but deriving guarantees the
    // column sums to exactly the total on screen rather than nearly. And the same field
    // is SLEEVE-relative on the account-scoped fallback below, so passing it through
    // would silently change meaning with the endpoint.
    reportedPctOfAccount: derivePctOfAccount(marketValue, accountTotal),
    // Populated by the portfolio-scoped endpoint; null on every row of the account-scoped
    // fallback, securities included. The store then falls back to marketValue - totalCost,
    // which is also null there, so the column reads N/A on that path only.
    //
    // ⚠️ **A zero cost basis is passed through as a real zero, by decision.** Only null
    // reads as "not on file". Worth knowing: on the portfolio-scoped endpoint Orion
    // appears to report an unavailable basis as `0` rather than null — on 4477-1166 both
    // holdings came back `costAmount: 0`, which renders Total Cost $0.00 and a gain equal
    // to the whole market value (TSLA: +$25,481.22). The same account reads N/A in Initial
    // Account Setup, whose account-scoped endpoint returns null instead, so the two
    // wizards disagree on one account. Confirm with the trade team whether a zero is ever
    // legitimate before changing this; the call for now is to show what Orion reports.
    reportedGainLoss: h.unrealizedGainLoss ?? null,
  };
}

/**
 * Per-share price implied by the value being displayed, or null when the row has no
 * share count to divide by — cash-like rows, where the caller keeps Orion's own price.
 */
function derivePrice(marketValue: number, shares: number | undefined): number | null {
  if (!shares) return null;
  return marketValue / shares;
}

/** A holding's share of the whole account, or null when there is no total to divide by. */
function derivePctOfAccount(marketValue: number, accountTotal: number): number | null {
  if (!accountTotal) return null;
  return (marketValue / accountTotal) * 100;
}

/**
 * Static demo payload used only when no account is supplied (tests / no
 * selection). `models` is empty because real models come from the API.
 */
export const MOCK_REALLOCATION_V2_DATA: ReallocationV2Data = {
  account: {
    custodian: 'Charles Schwab',
    accountNumber: '56985986',
    clientName: 'Valued Client',
    totalValue: 36555.31,
  },
  positions: [
    { securityId: 9001, securityTypeId: 5, requiresReview: false, ticker: 'SGOV', description: 'iShares 0-3 Month Treasury Bond ETF', shares: 100, price: 50.42, marketValue: 5042.0, totalCost: 4800.0, isCash: false, assetCategoryName: 'Bond', sellable: true, dntEligible: true },
    { securityId: 9002, securityTypeId: 5, requiresReview: false, ticker: 'APPL', description: 'Apple Inc.', shares: 569, price: 189.3, marketValue: 25659.68, totalCost: 18200.0, isCash: false, assetCategoryName: 'Equity', sellable: true, dntEligible: true },
    { securityId: 9003, securityTypeId: 5, requiresReview: false, ticker: 'MSFT', description: 'Microsoft Corporation', shares: 5, price: 415.22, marketValue: 2076.1, totalCost: 2400.0, isCash: false, assetCategoryName: 'Equity', sellable: true, dntEligible: true },
  ],
  // Cash is split out of the selectable list; it becomes the protected-cash
  // line of the Custom Holdings sleeve when the advisor sets any aside.
  cashPositions: [
    { securityId: 9000, securityTypeId: 2, requiresReview: false, ticker: 'CASH', description: 'Custodial Cash', shares: 3777.53, price: 1.0, marketValue: 3777.53, totalCost: null, isCash: true, assetCategoryName: 'Money Market', sellable: false, dntEligible: false },
  ],
  models: [
    { id: 'RFGG', name: 'Risk-Focused Global Growth', modelAggId: null, isSma: false },
    { id: 'SN', name: 'Strategic Neutral', modelAggId: null, isSma: false },
  ],
};
